import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormioService } from '@pics-core/dynamic';
import { finalize, Subject, takeUntil } from 'rxjs';
import { AlertService } from '@app/@core/services/alert.service';
import { HttpService } from '@app/@core/services/http.service';
import { LocalService } from '@app/@core/services/local.service';
import { FormioCustomComponent, FormioEvent } from '@app/shared/elements.common';
import { PageBuilderAddService } from '../@core/page-builder-add.service';
import {
  SupportingDocumentGridConfig,
  SupportingDocumentGridOption,
  SupportingDocumentGridRequirement,
  SupportingDocumentGridStatusConfig,
  SupportingDocumentRuleDefinition
} from './supporting-document-grid-config.model';
import { SupportingDocumentGridConfigService } from './supporting-document-grid-config.service';
import { SupportingDocumentGridRuleService } from './supporting-document-grid-rule.service';
import { SupportingDocumentRuleConfigService } from '../supporting-document-rule-config/supporting-document-rule-config.service';
import { DynamicDocumentBridgeService, DynamicDocumentGridState } from '../dynamic-document-bridge.service';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import {
  getCancelFileUploadModalConfig,
  isCancelFileUploadConfirmed
} from '@app/shared/constants/cancel-file-upload-modal.util';

interface SupportingDocumentRow {
  document_id?: number | null;
  rule_id?: number | null;
  application_id?: number | string | null;
  person_id?: number | string | null;
  formid?: number | string | null;
  cd_document_category?: string;
  cd_document_type?: string;
  file_name?: string;
  file_hash?: string;
  path?: string;
  status?: string;
  document_notes?: string;
  section_name?: string;
  created_by?: number | string | null;
  created_by_name?: string;
  updated_by?: number | string | null;
  uploaded_at?: any;
  is_active?: boolean;
  additional_info?: any;
  additionalInfo?: any;
  mirrored_from_document_id?: number | string | null;
  mirroredFromDocumentId?: number | string | null;
  is_system_generated?: boolean;
  is_profile_document?: boolean;
  isSystemGenerated?: boolean;
  old_id?: string | null;
  isPlaceholder?: boolean;
  _placeholderKey?: string;
  _lockType?: boolean;
  dynamic_source_key?: string;
  dynamic_rule_id?: string;
  dynamic_type_lookup_request_type?: string;
  _dynamicSourceKey?: string;
  _dynamicRuleId?: string;
  _dynamicTypeLookupRequestType?: string;
  _optionalRule?: boolean;
  _categoryIsAll?: boolean;
  _showAdditionalCategory?: boolean;
  is_new?: boolean | string | number;
  is_read_only?: boolean | string | number;
  is_highlighted?: boolean | string | number;
  is_highlighted_update?: boolean | string | number;
}

interface SupportingDocumentDependentRequirement {
  category?: string;
  type?: string;
  sectionName?: string;
  lockType?: boolean;
  placeholderCount?: number;
}

interface SupportingDocumentSyncDefinition {
  targetSection?: string;
  targetSectionName?: string;
  target_category?: string;
  targetCategory?: string;
  targetCategoryKey?: string;
  targetCategoryId?: string;
  target_type?: string;
  targetType?: string;
  targetTypeKey?: string;
  targetTypeId?: string;
  [key: string]: any;
}

interface SupportingDocumentGridValue {
  documents: SupportingDocumentRow[];
  activeDocuments: SupportingDocumentRow[];
  archivedDocuments: SupportingDocumentRow[];
  currentSectionName: string;
  missingRequiredDocuments: SupportingDocumentRow[];
  hasMissingRequiredDocuments: boolean;
  hasMissedRequiredDocuments: boolean;
  showMissingRequiredDocumentsError: boolean;
  canProceed: boolean;
}

@Component({
  standalone: false,
  selector: 'app-supporting-document-grid',
  templateUrl: './supporting-document-grid.component.html',
  styleUrls: ['./supporting-document-grid.component.scss']
})
export class SupportingDocumentGridComponent implements OnInit, OnChanges, OnDestroy, FormioCustomComponent<any> {
  @ViewChild('duplicateApplicantModal', { static: false })
  duplicateApplicantModalTemplate!: TemplateRef<any>;

  @ViewChild('duplicateStaffModal', { static: false })
  duplicateStaffModalTemplate!: TemplateRef<any>;

  @ViewChild('confirmValidModal', { static: false })
  confirmValidModalTemplate!: TemplateRef<any>;

  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();
  @Input() disabled: boolean = false;
  @Output() formioEvent = new EventEmitter<FormioEvent>();
  @Input() name: string = '';
  @Input() buttonLabel: string = '';
  @Input() formId: string = '';
  @Input() gridId: string = '';
  @Input() query: any;
  @Input() fields: any;
  @Input() configJson: any;
  @Input() readOnlyCondition = '';
  @Input() showArchived = '';
  @Input() allowedExtensions: any;
  @Input() maxFileSizeMb: number = 10;
  @Input() requireCategory: boolean = false;
  @Input() requireType: boolean = false;
  @Input() archiveEnabledExpression: string = '';
  @Input() archivedStatuses: any;
  @Input() duplicateMatchOn: any;
  @Input() duplicateAllowOverrideExpression: string = '';
  @Input() duplicateBlockMessageStaff: string = '';
  @Input() duplicateBlockMessageApplicant: string = '';
  @Input() uploadActionExpression: string = '';
  @Input() viewActionExpression: string = '';
  @Input() downloadActionExpression: string = '';
  @Input() deleteActionExpression: string = '';
  @Input() replaceActionExpression: string = '';
  @Input() markValidActionExpression: string = '';
  @Input() markDeficientActionExpression: string = '';
  @Input() markAddressedActionExpression: string = '';
  @Input() viewDeficiencyActionExpression: string = '';
  @Input() categoriesConfig: any;
  @Input() documentTypesConfig: any;
  @Input() requirementsConfig: any;
  @Input() rulesEndpoint: string = '';
  @Input() stepperId: string = '';
  @Input() pageId: string = '';
  @Input() panelKey: string = '';
  @Input() listEndpoint: string = '';
  @Input() saveEndpoint: string = '';
  @Input() updateStatusEndpoint: string = '';
  @Input() deleteEndpoint: string = '';
  @Input() replaceEndpoint: string = '';
  @Input() syncSaveEndpoint: string = '';
  @Input() syncDeleteEndpoint: string = '';
  @Input() pendingReviewStatus: string = '';
  @Input() validatedStatus: string = '';
  @Input() deficientStatus: string = '';
  @Input() archivedStatus: string = '';
  @Input() deletedStatus: string = '';
  @Input() isDynamicTypeDropdownNeeded: boolean | string = false;
  @Input() dynamicDocumentSourceGridKey: string = '';
  @Input() dynamicDocumentSourceRecordKeyField: string = '';
  @Input() dynamicDocumentSourceTypeField: string = '';
  @Input() dynamicDocumentSourceTypeLookupRequestType: string = '';
  @Input() showOnlyAvailableDocuments: boolean | string = false;
  @Input() showAdditionalUploadInViewOnly: boolean | string = false;
  @Input() showDeleteButtonInViewOnly: boolean | string = false;
  @Input() hideUploadConditionEnabled: boolean | string = false;
  @Input() hideUploadConditionExpression: string = '';
  @Input() isAdditionalDynamicTypeNeeded: boolean | string = false;
  @Input() additionalDynamicTypeSourceGridKey: string = '';
  @Input() additionalDynamicTypeSourceFieldName: string = '';
  @Input() additionalDynamicTypeCategoryFieldName: string = '';
  @Input() showOnlyConditionEnabled: boolean | string = false;
  @Input() showOnlyConditionExpression: string = '';
  @Input() showOnlyCategoryConditionEnabled: boolean | string = false;
  @Input() showOnlyCategoryName: string = '';
  @Input() showOnlyCategoryConditionExpression: string = '';
  @Input() caseDocEnabled: boolean | string = false;

  config: SupportingDocumentGridConfig;
  rawDocuments: SupportingDocumentRow[] = [];
  visibleDocuments: SupportingDocumentRow[] = [];
  activeDocuments: SupportingDocumentRow[] = [];
  archivedDocuments: SupportingDocumentRow[] = [];
  activeTab: 'active' | 'archived' = 'active';
  displayMode = 'compact';
  archiveEnabled = false;
  readOnly = false;
  showConfigHint = true;
  formData: any = {};
  uploadDraft = { category: '', docType: '', sectionName: '' };
  initialUploadDraft = { category: '', docType: '', sectionName: '' };
  currentUploadTypes: SupportingDocumentGridOption[] = [];
  selectedFile: File | null = null;
  uploadError = '';
  uploadCategoryError = '';
  uploadTypeError = '';
  uploadFileTouched = false;
  uploadCategoryTouched = false;
  uploadTypeTouched = false;
  uploadSaveAttempted = false;
  readonly needDragAndDrop = true;
  uploadTargetRow: SupportingDocumentRow | null = null;
  uploadMode: 'create' | 'replace' = 'create';
  deficiencyTarget: SupportingDocumentRow | null = null;
  deficiencyNotes = '';
  deficiencyError = '';
  previewDocument: SupportingDocumentRow | null = null;
  previewUrl: SafeResourceUrl | null = null;
  dbRules: SupportingDocumentRuleDefinition[] = [];
  pendingSourceUploadModal: NgbModalRef | null = null;
  duplicateModalRef: NgbModalRef | null = null;
  deleteModalRef: NgbModalRef | null = null;
  deleteTargetRow: SupportingDocumentRow | null = null;
  confirmValidModalRef: NgbModalRef | null = null;
  validTargetRow: SupportingDocumentRow | null = null;
  lookupCategories: SupportingDocumentGridOption[] = [];
  lookupDocumentTypes: SupportingDocumentGridOption[] = [];
  resolvedDocumentStatuses: SupportingDocumentGridStatusConfig | null = null;
  placeholderMessage: Record<string, string> = {};
  errorMessage: Record<string, string> = {};
  lookupTypesByParentId: Record<string, SupportingDocumentGridOption[]> = {};
  placeholderTypeSelections: Record<string, string> = {};
   // Persists the user's in-grid category selection for ALL-category placeholders
  // so re-renders triggered by emitValue/ngOnChanges don't reset the dropdown.
  placeholderCategorySelections: Record<string, string> = {};
  showMissingRequiredDocumentsError = false;
  private dynamicSourceRecords: any[] = [];
  private dynamicSourceKeys = new Set<string>();
  private dynamicTypeLookupOptions: Record<string, SupportingDocumentGridOption[]> = {};
  private dynamicTypeLookupLoading: Record<string, boolean> = {};
  private additionalDynamicTypeRecords: any[] = [];
  private additionalDynamicTypesByCategory: Record<string, SupportingDocumentGridOption[]> = {};
  // Case Document Mode: instead of populating placeholder rows in the grid for
  // matched rules, the matched categories/types are collected here and surfaced
  // only inside the (additional) upload modal. The grid stays empty until the
  // user actually uploads a document.
  private caseDocUploadRequirements: Array<{ category: string; type: string }> = [];
  private caseDocHasAllCategoryRequirement = false;
  private cachedUploadTypeOptionsCategory = '';
  private cachedUploadTypeOptionsBase: SupportingDocumentGridOption[] | null = null;
  private cachedUploadTypeOptionsResult: SupportingDocumentGridOption[] = [];
  private readonly wizardNavigationClassPattern = /\b(btn-wizard-nav-next|btn-wizard-nav-submit|formio-button-next|formio-button-submit)\b/i;
  private navigationValidationSyncTimers: number[] = [];
  private navigationButtonsWithValidation = new Set<HTMLButtonElement>();
  private typeLoadInProgress: Record<string, boolean> = {};
  private lastRulesRequestKey = '';
  private lastDocumentsRequestKey = '';
  private activeDynamicSourceGridKey = '';
  private cachedDynamicUploadTypes: SupportingDocumentGridOption[] | null = null;
  private cachedDynamicUploadTypesSignature = '';
  private syncedSourceOrderBySection: Record<string, string[]> = {};
  private syncedTargetRowsBySection: Record<string, string[]> = {};
  private pendingSyncedTargetRowsBeforeReload: Record<string, string[]> = {};
  private readonly handleNavigationValidationClick = (event: Event) => this.validateRequiredDocumentsBeforeNavigation(event);
  private readonly handleSyncDocumentsChanged = (event: Event) => this.onSyncDocumentsChanged(event);

  private destroy$ = new Subject<void>();
  actionText: string = "";
  timelineIntakeStatusCount: any;
  latestIntakeOccuredDate: any;
  constructor(
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: NgbModal,
    private readonly alertService: AlertService,
    private readonly formIoService: FormioService,
    private readonly pageBuilderService: PageBuilderAddService,
    private readonly httpService: HttpService,
    private readonly sessionStorage: LocalService,
    private readonly configService: SupportingDocumentGridConfigService,
    private readonly ruleService: SupportingDocumentGridRuleService,
    private readonly supportingDocumentRuleConfigService: SupportingDocumentRuleConfigService,
    private readonly sanitizer: DomSanitizer,
    private readonly dynamicDocumentBridge: DynamicDocumentBridgeService
  ) {
    this.config = this.configService.parse(this.buildConfigInputs(), {
      readOnlyExpression: this.readOnlyCondition,
      showArchivedExpression: this.showArchived
    });
  }

  ngOnInit(): void {
    this.formData = this.formIoService.getCurrentFormData() || {};
    this.rebuildConfig();
    this.syncDynamicSourceRecordsFromFormData();
    this.syncDynamicSourceRecordsFromBridge();
    this.syncAdditionalDynamicTypeRecords();
    this.bindDynamicDocumentBridge();
    this.loadDocumentStatuses();
    this.loadLookupData();
    this.loadInitialDocuments();
    this.loadRules();
    window.addEventListener('supporting-documents-sync-changed', this.handleSyncDocumentsChanged);
    this.formIoService.getFormData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        if (!value) {
          return;
        }
        this.formData = value?.data ? value : { data: value };
        this.syncDynamicSourceRecordsFromFormData();
        this.syncDynamicSourceRecordsFromBridge();
        this.syncAdditionalDynamicTypeRecords();
        this.loadInitialDocuments();
        this.rebuildDerivedState();
        this.loadRules();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasConfigScopeChanges =
      changes['configJson']
      || changes['readOnlyCondition']
      || changes['showArchived']
      || changes['archiveEnabledExpression']
      || changes['rulesEndpoint']
      || changes['listEndpoint']
      || changes['stepperId']
      || changes['pageId']
      || changes['panelKey']
      || changes['isDynamicTypeDropdownNeeded']
      || changes['dynamicDocumentSourceGridKey']
      || changes['dynamicDocumentSourceRecordKeyField']
      || changes['dynamicDocumentSourceTypeField']
      || changes['dynamicDocumentSourceTypeLookupRequestType']
      || changes['showOnlyAvailableDocuments']
      || changes['showAdditionalUploadInViewOnly']
      || changes['showDeleteButtonInViewOnly']
      || changes['hideUploadConditionEnabled']
      || changes['hideUploadConditionExpression']
      || changes['isAdditionalDynamicTypeNeeded']
      || changes['additionalDynamicTypeSourceGridKey']
      || changes['additionalDynamicTypeSourceFieldName']
      || changes['additionalDynamicTypeCategoryFieldName']
      || changes['showOnlyConditionEnabled']
      || changes['showOnlyConditionExpression']
      || changes['showOnlyCategoryConditionEnabled']
      || changes['showOnlyCategoryName']
      || changes['showOnlyCategoryConditionExpression']
      || changes['caseDocEnabled'];

    if (hasConfigScopeChanges) {
      this.rebuildConfig();
      this.syncDynamicSourceRecordsFromFormData();
      this.syncDynamicSourceRecordsFromBridge();
      this.syncAdditionalDynamicTypeRecords();
      this.bindDynamicDocumentBridge();
      this.loadInitialDocuments();
      this.loadRules();
    }

    if (changes['value'] && !changes['value'].firstChange) {
      const incomingRows = this.normalizeIncomingDocuments(this.value);
      // In view-only mode the documents are loaded from the API (by
      // application + section), not from the form value. Don't let an empty
      // incoming value (e.g. a read-only form passing the default empty value)
      // wipe the documents that were already loaded — otherwise the grid shows
      // empty in read-only mode even though documents exist.
      if (this.isShowOnlyAvailableDocumentsMode() && !incomingRows.length && this.rawDocuments.length) {
        this.rebuildDerivedState();
      } else {
        this.rawDocuments = incomingRows;
        this.rebuildDerivedState();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearNavigationValidationHandlers();
    window.removeEventListener('supporting-documents-sync-changed', this.handleSyncDocumentsChanged);
    this.destroy$.next();
    this.destroy$.complete();
  }

  refreshVisibleDocuments(): void {
    this.visibleDocuments = this.activeTab === 'archived' ? this.archivedDocuments : this.activeDocuments;
  }

  shouldShowGridComponent(): boolean {
    // When show-only condition is active and evaluates to false, hide the
    // component entirely regardless of rules/documents.
    // When it evaluates to true, fall through to normal visibility logic.
    if (this.isShowOnlyConditionActive() && !this.evaluateShowOnlyCondition()) {
      return false;
    }

    const hasCountableDocuments =
      this.activeDocuments.some((row: SupportingDocumentRow) => this.shouldCountRowForGridVisibility(row))
      || (this.archiveEnabled && this.archivedDocuments.some((row: SupportingDocumentRow) => this.shouldCountRowForGridVisibility(row)));

    // Case Document Mode: when there are no uploaded documents, only show the
    // (empty) document section if the upload button is actually visible. This
    // prevents an empty section with no upload button from rendering when the
    // "Hide Upload Button Condition" hides the button for the current role.
    if (this.isCaseDocMode()) {
      // Documents present, or read-only viewing → always keep the section
      // visible (read-only mode should still display the grid).
      if (hasCountableDocuments || this.readOnly) {
        return true;
      }
      // No uploaded documents (editable). Hide the empty section only when the
      // upload button is suppressed by the Hide Upload Button Condition, so we
      // don't render an empty section without an upload button.
      if (this.hasCaseDocUploadRequirements()) {
        return !this.isUploadHiddenByCondition();
      }
      return this.hasAlwaysShowActiveRule();
    }

    return hasCountableDocuments
      || this.hasAlwaysShowActiveRule();
  }

  /**
   * Returns true when Case Document Mode collected at least one upload
   * requirement from matched rules. In that case the grid section (and its
   * upload button) must stay visible even though no placeholder rows exist,
   * so the user can open the upload modal for the matched categories.
   */
  private hasCaseDocUploadRequirements(): boolean {
    return this.caseDocUploadRequirements.length > 0 || this.caseDocHasAllCategoryRequirement;
  }

  /**
   * Returns true when at least one in-scope rule has show_always_default = true.
   * Such rules cause the grid to render even when no documents/placeholders exist,
   * so the user can still see the upload button and optionally upload.
   */
  hasAlwaysShowActiveRule(): boolean {
    return (this.dbRules || []).some((rule: SupportingDocumentRuleDefinition) =>
      !!rule?.show_always_default && this.ruleMatchesScope(rule) && this.matchesShowAlwaysRoleCondition(rule)
    );
  }


  hasAllCategoryActiveRule(): boolean {
    return (this.dbRules || []).some((rule: SupportingDocumentRuleDefinition) =>
      this.isAllCategoryValue(rule?.cd_document_category) && this.ruleMatchesScope(rule)
    );
  }

  canShowToolbarUpload(): boolean {
    if (this.isShowOnlyAvailableDocumentsMode()) {
      // The readOnly condition (e.g. read-only workflow state) must take
      // precedence over the "Show Additional Upload Button in View Only"
      // feature, otherwise the upload button stays active in read-only mode.
      return !this.readOnly && this.isAdditionalUploadInViewOnlyEnabled();
    }
    if (this.isUploadHiddenByCondition()) {
      return false;
    }
    return !this.readOnly && this.evaluateAction(this.config.actions.upload || '!readOnly', undefined, ['upload', 'showUpload']);
  }

  canUploadRow(row: SupportingDocumentRow): boolean {
    if (!this.canUseEditableDocumentAction(row)) {
      return false;
    }
    return !!row
      && !row.file_name
      && this.evaluateAction(this.config.actions.upload || '!readOnly', row, ['upload', 'showUpload']);
  }

  canView(row: SupportingDocumentRow): boolean {
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.view || '!!row.file_name', row, ['view', 'showView']);
  }

  canDownload(row: SupportingDocumentRow): boolean {
    if (this.isShowOnlyAvailableDocumentsMode()) {
      return this.hasUploadedFile(row);
    }
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.download || '!!row.file_name', row, ['download', 'showDownload']);
  }

  canDelete(row: SupportingDocumentRow): boolean {
    if (this.isShowOnlyAvailableDocumentsMode()) {
      // The readOnly condition must take precedence over the view-only
      // delete/additional-upload features so nothing is editable in read-only mode.
      if (this.readOnly) {
        return false;
      }
      if (this.isDeleteButtonInViewOnlyEnabled() && this.hasUploadedFile(row)) {
        return true;
      }
      if (this.isAdditionalUploadInViewOnlyEnabled() && this.hasUploadedFile(row)) {
        const currentFormId = String(this.resolveEffectiveFormId() || '').trim();
        const rowFormId = String(row.formid || '').trim();
        return !!currentFormId && rowFormId === currentFormId;
      }
      return false;
    }
    if (!this.canUseEditableDocumentAction(row)) {
      return false;
    }
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.delete || '!row.isPlaceholder && !readOnly', row, ['delete', 'showDelete']);
  }

  canReplace(row: SupportingDocumentRow): boolean {
    if (!this.canUseEditableDocumentAction(row)) {
      return false;
    }
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.replace || '', row, ['replace', 'showReplace']);
  }

  canMarkValid(row: SupportingDocumentRow): boolean {
    if (this.isCaseDocMode()) {
      return false;
    }
    if (!this.canUseEditableDocumentAction(row)) {
      return false;
    }
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.markValid || '', row, ['markValid', 'showMarkValid']);
  }

  canMarkDeficient(row: SupportingDocumentRow): boolean {
    if (this.isCaseDocMode()) {
      return false;
    }
    if (!this.canUseEditableDocumentAction(row)) {
      return false;
    }
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.markDeficient || '', row, ['markDeficient', 'showMarkDeficient']);
  }

  // canMarkAddressed(row: SupportingDocumentRow): boolean {
  //   if (this.isShowOnlyAvailableDocumentsMode()) {
  //   if (this.isDocumentReadOnly(row)) {
  //     return false;
  //   }
  //     return false;
  //   }
  //   return this.evaluateAction(this.config.actions.markAddressed || '', row, ['markAddressed', 'showMarkAddressed']);
  // }

  canViewDeficiency(row: SupportingDocumentRow): boolean {
    if (this.isCaseDocMode()) {
      return false;
    }
    if (!this.canUseEditableDocumentAction(row)) {
      return false;
    }
    return this.hasUploadedFile(row)
      && this.evaluateAction(this.config.actions.viewDeficiency || '', row, ['viewDeficiency', 'showViewDeficiency']);
  }

  private canUseEditableDocumentAction(row: SupportingDocumentRow): boolean {
    return !!row
      && !this.isShowOnlyAvailableDocumentsMode()
      && !this.isDocumentReadOnly(row)
      && !this.isSystemManagedDocument(row);
  }


  /**
   * Returns true when the formio "Show Only Available Documents" option is enabled.
   * In this mode the grid loads documents using only application_id + section_name
   * (panel key) and exposes only the View and Download row actions.
   */
  isShowOnlyAvailableDocumentsMode(): boolean {
    const value = this.showOnlyAvailableDocuments;
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  /**
   * Returns true when "Show Additional Upload Button" is enabled in view-only mode.
   */
  isAdditionalUploadInViewOnlyEnabled(): boolean {
    const value = this.showAdditionalUploadInViewOnly;
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  isDeleteButtonInViewOnlyEnabled(): boolean {
    const value = this.showDeleteButtonInViewOnly;
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  /**
   * Returns true when the "Hide Upload Button Condition" is enabled and the
   * expression evaluates to true. This is ignored when View Only Mode is active.
   */
  private isUploadHiddenByCondition(): boolean {
    const enabled = this.hideUploadConditionEnabled;
    const isEnabled = typeof enabled === 'boolean' ? enabled : String(enabled || '').trim().toLowerCase() === 'true';
    if (!isEnabled) {
      return false;
    }
    const expression = (this.hideUploadConditionExpression || '').trim();
    if (!expression) {
      return false;
    }
    return this.evaluateBoolean(expression, false);
  }

  /**
   * Returns true when the "Show Only Condition" toggle is enabled in the formio configuration.
   */
  isShowOnlyConditionActive(): boolean {
    const value = this.showOnlyConditionEnabled;
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  /**
   * Evaluates the "Show Only Condition Expression" to determine component visibility.
   * When the expression returns true the component is shown; when false it is hidden.
   * This overrides all other visibility logic (rules, documents, placeholders, etc.).
   */
  private evaluateShowOnlyCondition(): boolean {
    const expression = (this.showOnlyConditionExpression || '').trim();
    if (!expression) {
      return true;
    }
    return this.evaluateBoolean(expression, true);
  }

  isShowOnlyCategoryConditionActive(): boolean {
    const value = this.showOnlyCategoryConditionEnabled;
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  private isCategoryAllowedForCurrentUser(categoryKey: string): boolean {
    if (!this.isShowOnlyCategoryConditionActive()) {
      return true;
    }
    const targetCategory = (this.showOnlyCategoryName || '').trim();
    if (!targetCategory) {
      return true;
    }
    if (!this.isSameLookupValue(categoryKey, targetCategory)) {
      return true;
    }
    const expression = (this.showOnlyCategoryConditionExpression || '').trim();
    if (!expression) {
      return true;
    }
    return this.evaluateBoolean(expression, true);
  }

  closePopup() {
    this.actionText = "";
    this.modalService.dismissAll();
  }

  canEditRowType(row: SupportingDocumentRow): boolean {
    return !!row?.isPlaceholder && !row?._lockType && !this.readOnly && this.getTypesForRow(row).length > 0;
  }
/**
   * Returns true when this row is a placeholder created from a rule whose
   * category is "ALL". In that case the user must choose a specific category
   * (from the section's allowed categories) before uploading, and the saved
   * document persists the picked category key.
   *
   * Detection is intentionally permissive: either the explicit `_categoryIsAll`
   * flag set by `buildPlaceholderRowsFromDbRules`, or a row that still carries
   * the literal sentinel value 'ALL' in `cd_document_category` (e.g. from a
   * legacy row, or before the placeholder builder has run) is treated as an
   * editable-category placeholder.
   */
  canEditRowCategory(row: SupportingDocumentRow): boolean {
    if (!row?.isPlaceholder || this.readOnly) {
      return false;
    }
    return !!row?._categoryIsAll || this.isAllCategoryValue(row?.cd_document_category);
  }

  private isAllCategoryValue(value: any): boolean {
    return String(value || '').trim().toUpperCase() === 'ALL';
  }

  getCategoriesForRow(row: SupportingDocumentRow): SupportingDocumentGridOption[] {
    return this.getAvailableCategories(!!row?._showAdditionalCategory);
  }

  /**
   * Value to bind to the in-grid category <select>. Strips the sentinel 'ALL'
   * so the dropdown starts on the "Select Category" placeholder instead of
   * trying to select a non-existent option.
   */
  getRowCategoryValue(row: SupportingDocumentRow): string {
    return this.isAllCategoryValue(row?.cd_document_category) ? '' : String(row?.cd_document_category || '');
  }

  onRowCategoryChange(row: SupportingDocumentRow, value: string): void {
    this.showMissingRequiredDocumentsError = true;
    row.cd_document_category = value;
    // Only reset the type when it isn't locked. For dynamic-document rows the
    // type is derived from the source record (e.g. district name) and is locked
    // (`_lockType: true`); resetting it would blank out a value the user can't
    // re-enter.
    if (!row?._lockType) {
      row.cd_document_type = '';
      if (row?._placeholderKey) {
        delete this.placeholderTypeSelections[row._placeholderKey];
      }
    }
    if (row?._placeholderKey && row._categoryIsAll) {
      // Persist the picked category for ALL-category placeholders so any future
      // rebuild can restore it.
      if (value) {
        this.placeholderCategorySelections[row._placeholderKey] = value;
      } else {
        delete this.placeholderCategorySelections[row._placeholderKey];
      }
    }
    if (value) {
      this.ensureCategoryTypesLoaded(value);
    }
    // IMPORTANT: do NOT call emitValue() here.
    // The in-grid category is a UI-only selection that drives the upload modal;
    // it isn't persisted until the user actually uploads a file. Emitting here
    // triggers the parent's value change -> ngOnChanges -> normalizeIncomingDocuments
    // -> rebuildDerivedState cycle, which rebuilds the placeholder rows and
    // visually resets the dropdown to "Select Category" within a fraction of a
    // second. The selection still lives on the row object and in
    // placeholderCategorySelections, so the upload flow will pick it up.
    this.cdr.detectChanges();
  }

  isRowCurrentlyRequired(row: SupportingDocumentRow): boolean {
    if (!row) {
      return false;
    }
    if (row._optionalRule) {
      return false;
    }
    const dynamicSourceKey = this.getRowDynamicSourceKey(row);
    if (dynamicSourceKey && !this.dynamicSourceKeys.has(dynamicSourceKey)) {
      return false;
    }
    if (dynamicSourceKey && this.dynamicSourceKeys.has(dynamicSourceKey)) {
      return true;
    }
    if (this.hasUploadedFile(row) && !row._placeholderKey && !this.isRequiredUploadedRow(row)) {
      return false;
    }
    if (this.isRequiredPlaceholderRow(row)) {
      return true;
    }
    if (this.dbRules.length) {
      return this.matchesActiveDbRule(row);
    }
    return this.matchesActiveConfiguredRequirement(row);
  }

  onRowTypeChange(row: SupportingDocumentRow, value: string): void {
    this.showMissingRequiredDocumentsError = true;
    row.cd_document_type = value;
    if (row?._placeholderKey) {
      this.placeholderTypeSelections[row._placeholderKey] = value;
    }
    this.rebuildDerivedState();
  }

  onUploadCategoryChange(value: string): void {
    this.uploadDraft.category = value;
    this.uploadCategoryError = '';
    this.uploadTypeError = '';
    this.ensureCategoryTypesLoaded(value);
    this.syncUploadTypes(value);
  }

  onUploadTypeChange(value: string): void {
    this.uploadDraft.docType = value;
    this.uploadTypeError = '';
  }

  markUploadCategoryTouched(): void {
    this.uploadCategoryTouched = true;
  }

  markUploadTypeTouched(): void {
    this.uploadTypeTouched = true;
  }

  markUploadFileTouched(): void {
    this.uploadFileTouched = true;
  }

  getTypesForRow(row: SupportingDocumentRow): SupportingDocumentGridOption[] {
    // If dynamic type dropdown is needed and the row carries a dynamic lookup request type,
    // surface the dynamic lookup options (same source the grid cell editor relies on).
    if (this.isDynamicTypeDropdownEnabled()) {
      const requestType = this.getRowDynamicTypeLookupRequestType(row);
      if (requestType) {
        this.ensureDynamicTypeLookupLoaded(requestType);
        const dynamicOptions = this.dynamicTypeLookupOptions[requestType] || [];
        if (dynamicOptions.length) {
          return this.sortDocumentTypeOptions(dynamicOptions);
        }
      }
    }
    this.ensureCategoryTypesLoaded(row?.cd_document_category || '');
    return this.getDocumentTypes(row?.cd_document_category || '');
  }

  /**
   * Returns true when the Dynamic Type Dropdown feature is enabled via either
   * the `isDynamicTypeDropdownNeeded` input or the flattened config
   * (`config.dynamicDocuments.enabled`). Accepts boolean or string ('true'/'false').
   */
  isDynamicTypeDropdownEnabled(): boolean {
    const fromInput = this.isDynamicTypeDropdownNeeded;
    const fromConfig = this.config?.dynamicDocuments?.enabled;
    const normalize = (value: any): boolean => {
      if (typeof value === 'boolean') {
        return value;
      }
      return String(value || '').trim().toLowerCase() === 'true';
    };
    return normalize(fromInput) || normalize(fromConfig);
  }

  private hasDocumentSourceGrid(): boolean {
    return !!String(this.config?.dynamicDocuments?.sourceGridKey || '').trim();
  }

  /**
   * Returns the options to display in the upload modal Type dropdown.
   * When the dynamic type dropdown feature is enabled, this mirrors the types
   * shown in the grid cell editor by using the dynamic lookup request type.
   * When additional dynamic type is enabled, merges those types into the
   * result filtered by the currently selected upload category.
   */
  getUploadTypeOptions(): SupportingDocumentGridOption[] {
    // Case Document Mode: restrict the Type list to the types collected from
    // matched rules for the currently selected category.
    if (this.isCaseDocMode() && this.getCaseDocRequirementCategories().length && !this.caseDocHasAllCategoryRequirement) {
      return this.getCaseDocTypeOptionsForCategory(this.resolveUploadCategory());
    }
    if (this.isDynamicTypeDropdownEnabled()) {
      const dynamicOptionsFromRows = this.buildUploadTypeOptionsFromRows();
      if (dynamicOptionsFromRows.length) {
        return this.mergeAdditionalDynamicTypes(dynamicOptionsFromRows);
      }
    }
    return this.mergeAdditionalDynamicTypes(this.currentUploadTypes);
  }

  /**
   * Merges additional dynamic type options (filtered by the selected upload
   * category) into the base type options array, deduplicating by key.
   */
  private mergeAdditionalDynamicTypes(baseOptions: SupportingDocumentGridOption[]): SupportingDocumentGridOption[] {
    if (!this.isAdditionalDynamicTypeEnabled()) {
      return baseOptions;
    }

    const selectedCategory = this.resolveUploadCategory();

    // Return cached result if inputs haven't changed (avoids creating new
    // array references on every change-detection tick which freezes the UI).
    if (this.cachedUploadTypeOptionsBase === baseOptions && this.cachedUploadTypeOptionsCategory === selectedCategory) {
      return this.cachedUploadTypeOptionsResult;
    }

    const additionalTypes = this.getAdditionalDynamicTypesForCategory(selectedCategory);
    if (!additionalTypes.length) {
      this.cachedUploadTypeOptionsBase = baseOptions;
      this.cachedUploadTypeOptionsCategory = selectedCategory;
      this.cachedUploadTypeOptionsResult = baseOptions;
      return baseOptions;
    }

    const seen = new Set<string>(baseOptions.map((item: SupportingDocumentGridOption) => item.key.toLowerCase()));
    const merged = [...baseOptions];

    for (const type of additionalTypes) {
      const dedupeKey = type.key.toLowerCase();
      if (!seen.has(dedupeKey)) {
        seen.add(dedupeKey);
        merged.push(type);
      }
    }

    const result = this.sortDocumentTypeOptions(merged);
    this.cachedUploadTypeOptionsBase = baseOptions;
    this.cachedUploadTypeOptionsCategory = selectedCategory;
    this.cachedUploadTypeOptionsResult = result;
    return result;
  }

  /**
   * Builds Type dropdown options for the upload modal when dynamic mode is on,
   * by collecting unique `cd_document_type` values from the grid's documents.
   * Memoized against the current `rawDocuments` reference so it does not
   * recompute (and does not allocate new arrays) on every change-detection tick,
   * which previously caused the page to freeze when the modal opened.
   */
  private buildUploadTypeOptionsFromRows(): SupportingDocumentGridOption[] {
    const sourceRows: SupportingDocumentRow[] = [
      ...(this.visibleDocuments || []),
      ...(this.activeDocuments || []),
      ...(this.rawDocuments || [])
    ];
    const selectedCategory = this.resolveUploadCategory();
    const ignoreCategoryFilter = this.hasAllCategoryActiveRule();
    const sourceSignature = sourceRows
      .map((row: SupportingDocumentRow) => `${row?.document_id || row?._placeholderKey || ''}:${row?.cd_document_category || ''}:${row?.cd_document_type || ''}`)
      .join('|');

    const cacheCategoryKey = ignoreCategoryFilter ? '__ALL__' : selectedCategory;
    if (this.cachedDynamicUploadTypesSignature === `${cacheCategoryKey}::${sourceSignature}` && this.cachedDynamicUploadTypes) {
      return this.cachedDynamicUploadTypes;
    }

    const seen = new Set<string>();
    const options: SupportingDocumentGridOption[] = [];
    for (const row of sourceRows) {
      if (!ignoreCategoryFilter && selectedCategory && !this.isSameLookupValue(row?.cd_document_category, selectedCategory)) {
        continue;
      }
      const key = String(row?.cd_document_type || '').trim();
      if (!key) {
        continue;
      }
      const dedupeKey = key.toLowerCase();
      if (seen.has(dedupeKey)) {
        continue;
      }
      seen.add(dedupeKey);
      // Use the row's own type value as the label to avoid invoking heavier
      // lookups (getTypeLabel/getDocumentTypes) on every CD tick.
      options.push({ key, value: key } as SupportingDocumentGridOption);
    }

    this.cachedDynamicUploadTypesSignature = `${cacheCategoryKey}::${sourceSignature}`;
    this.cachedDynamicUploadTypes = options;
    return options;
  }

  getCategoryLabel(row: SupportingDocumentRow): string {
     // Never surface the sentinel 'ALL' value in the grid; the dropdown
    // (rendered via canEditRowCategory) will let the user pick a real category.
    if (this.isAllCategoryValue(row?.cd_document_category)) {
      return '';
    }
    return this.resolveCategoryOption(row?.cd_document_category)?.value || row?.cd_document_category || '';
  }

  getTypeLabel(row: SupportingDocumentRow): string {
    const dynamicTypeLabel = this.getDynamicTypeLookupLabel(row);
    if (dynamicTypeLabel) {
      return dynamicTypeLabel;
    }

    const types = this.getDocumentTypes(row?.cd_document_category || '');
    const match = types.find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, row?.cd_document_type)
      || this.isSameLookupValue(item.value, row?.cd_document_type)
      || this.isSameLookupValue(item.lookupId, row?.cd_document_type)
    );
    if (match?.value) {
      return match.value;
    }
    const fallbackMatch = this.lookupDocumentTypes.find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, row?.cd_document_type)
      || this.isSameLookupValue(item.value, row?.cd_document_type)
      || this.isSameLookupValue(item.lookupId, row?.cd_document_type)
    );
    if (fallbackMatch?.value) {
      return fallbackMatch.value;
    }
    return row?.cd_document_type || '';
  }

  statusClass(status: string): string {
    const normalized = this.normalizeStatus(status);
    const caseStatuses = [
      this.config.statuses.case,
      'Case'
    ].map((item: any) => this.normalizeStatus(item));
    const pendingStatuses = [
      this.config.statuses.pendingReview,
      this.resolvedDocumentStatuses?.pendingReview,
      'Pending Review'
    ].map((item: any) => this.normalizeStatus(item));
    const verifiedStatuses = [
      this.config.statuses.validated,
      this.resolvedDocumentStatuses?.validated,
      'Verified',
      'Validated',
      'Valid'
    ].map((item: any) => this.normalizeStatus(item));
    const deficientStatuses = [
      this.config.statuses.deficient,
      this.resolvedDocumentStatuses?.deficient,
      'Deficient'
    ].map((item: any) => this.normalizeStatus(item));
    const archivedStatuses = [
      this.config.statuses.archived,
      this.resolvedDocumentStatuses?.archived,
      'Archived',
      'Replaced'
    ].map((item: any) => this.normalizeStatus(item));

    if (caseStatuses.includes(normalized)) {
      return 'verified';
    }
    if (pendingStatuses.includes(normalized)) {
      return 'pending-review';
    }
    if (verifiedStatuses.includes(normalized)) {
      return 'verified';
    }
    if (deficientStatuses.includes(normalized)) {
      return 'deficient';
    }
    if (archivedStatuses.includes(normalized)) {
      return 'archived';
    }
    return '';
  }

  formatGridDate(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
      return String(value).split(/[T\s]/)[0] || String(value);
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  openUploadModal(template: TemplateRef<any>, row?: SupportingDocumentRow): void {
    this.showMissingRequiredDocumentsError = true;
    this.uploadError = '';
    this.uploadCategoryError = '';
    this.uploadTypeError = '';
    this.uploadFileTouched = false;
    this.uploadCategoryTouched = false;
    this.uploadTypeTouched = false;
    this.uploadSaveAttempted = false;
    this.selectedFile = null;
    this.uploadTargetRow = row || null;
    this.uploadMode = row?.file_name ? 'replace' : 'create';
    this.uploadDraft = {
      category: row?.cd_document_category || '',
      docType: row?.cd_document_type || '',
      sectionName: row?.section_name || this.resolveSectionName(row)
    };
    this.syncUploadCategorySelection(true);
    this.ensureCategoryTypesLoaded(this.uploadDraft.category);
    this.syncUploadTypes(this.uploadDraft.category, true);
    this.initialUploadDraft = { ...this.uploadDraft };
    this.modalService.open(template, { size: 'lg', backdrop: 'static', centered: true });
  }

  openReplaceModal(template: TemplateRef<any>, row: SupportingDocumentRow): void {
    this.openUploadModal(template, row);
  }

  onFileSelected(event: any, fileUpload?: any): void {
    this.showMissingRequiredDocumentsError = true;
    this.uploadFileTouched = true;
    const files = Array.isArray(event?.files)
      ? event.files
      : Array.from((event?.target as HTMLInputElement)?.files || []);
    const selectedFile = files?.[0] || null;

    if (!selectedFile) {
      this.selectedFile = null;
      this.uploadError = '';
      return;
    }

    const validationError = this.validateFile(selectedFile);
    if (validationError) {
      this.selectedFile = null;
      this.uploadError = validationError;
      fileUpload?.clear?.();
      return;
    }

    this.selectedFile = selectedFile;
    this.uploadError = '';
    fileUpload?.clear?.();
  }

  handleFileUploadError(event: any): void {
    this.uploadFileTouched = true;
    const uploadedFile = event?.files?.[0] || null;
    const maxBytes = this.getUploadMaxFileSizeBytes();

    this.selectedFile = null;
    if (uploadedFile?.size && uploadedFile.size > maxBytes) {
      this.uploadError = this.getInvalidFileSizeMessage();
      return;
    }

    this.uploadError = this.getInvalidFileTypeMessage();
  }

  shouldShowTypeSelector(): boolean {
    const category = this.resolveUploadCategory();
    if (!category) {
      return true;
    }
    return this.getDocumentTypes(category).length > 0;
  }

  getUploadAcceptTypes(): string {
    return (this.config.validation.allowedExtensions || [])
      .map((extension: string) => `.${String(extension || '').replace(/^\./, '').toLowerCase()}`)
      .join(',');
  }

  getUploadMaxFileSizeBytes(): number {
    return Number(this.config.validation.maxFileSizeMb || 0) * 1024 * 1024;
  }

  getInvalidFileTypeMessage(): string {
    return this.errorMessage['M0129']
      || this.config.validation.invalidTypeMessage
      || 'The selected file type is not supported.';
  }

  getInvalidFileSizeMessage(): string {
    return this.errorMessage['M0128']
      || this.config.validation.maxSizeMessage
      || `The selected file exceeds the maximum allowed size of ${this.config.validation.maxFileSizeMb} MB.`;
  }

  getUploadFileInlineError(): string {
    if (!this.uploadFileTouched && !this.uploadSaveAttempted) {
      return '';
    }
    return this.uploadError
      || (!this.selectedFile ? this.errorMessage['M0196'] || this.config.validation.fileRequiredMessage || 'Please select a file.' : '');
  }

  getUploadCategoryInlineError(): string {
    if (!this.uploadCategoryTouched && !this.uploadSaveAttempted) {
      return '';
    }
    return this.uploadCategoryError
      || (!this.resolveUploadCategory() ? this.getCategoryRequiredMessage() : '');
  }

  getUploadTypeInlineError(): string {
    if (!this.uploadTypeTouched && !this.uploadSaveAttempted) {
      return '';
    }
    return this.uploadTypeError
      || (!this.resolveUploadDocType() ? this.getTypeRequiredMessage() : '');
  }

  shouldShowUploadTypeSelector(): boolean {
    return !this.uploadTargetRow || !String(this.uploadTargetRow?.cd_document_type || '').trim();
  }

  shouldShowUploadCategorySelector(): boolean {
    return !this.uploadTargetRow || !String(this.uploadTargetRow?.cd_document_category || '').trim();
  }

  isUploadCategoryDisabled(): boolean {

    if (this.hasAllCategoryActiveRule()) {
      return false;
    }
    return this.getAvailableCategories().length === 1;
  }

  isUploadTypeDisabled(): boolean {
    const typeOptions = this.getUploadTypeOptions();

    if (this.isDynamicTypeDropdownEnabled()) {
      return false;
    }
    if (this.hasAllCategoryActiveRule()) {
      return false;
    }
    if (!this.resolveUploadCategory()) {
      return true;
    }
    if (typeOptions.length === 1) {
      return true;
    }
    // When dynamic type dropdown is enabled, the Type list comes from a dynamic
    // lookup that is independent of the selected Category, so don't disable it.
    // if (this.isDynamicTypeDropdownEnabled() && typeOptions.length > 0) {
    //   return false;
    // }
    // When additional dynamic type is enabled and options are available for
    // the selected category, the Type dropdown should not be disabled.
    if (this.isAdditionalDynamicTypeEnabled() && typeOptions.length > 0) {
      return false;
    }
    return typeOptions.length === 0;
  }

  isUploadTypeRequired(): boolean {
    return true;
  }

  isUploadSaveDisabled(): boolean {
    return !this.selectedFile
      || !this.resolveUploadCategory()
      || !this.resolveUploadDocType()
      || !!this.uploadError;
  }

  async saveUpload(modal: NgbModalRef): Promise<void> {
    this.showMissingRequiredDocumentsError = true;
    this.uploadSaveAttempted = true;
    this.uploadError = '';
    this.uploadCategoryError = '';
    this.uploadTypeError = '';
    const resolvedCategory = this.resolveUploadCategory();
    this.syncUploadTypes(resolvedCategory, true);
    const resolvedDocType = this.resolveUploadDocType();
    const resolvedSectionName = String(this.resolveSectionName(this.uploadTargetRow) || '').trim();

    if (!this.selectedFile) {
      this.uploadError = this.errorMessage['M0196'] || this.config.validation.fileRequiredMessage || 'Please select a file.';
      return;
    }
    if (!resolvedCategory) {
      this.uploadCategoryError = this.getCategoryRequiredMessage();
      return;
    }
    if (!resolvedDocType) {
      this.uploadTypeError = this.getTypeRequiredMessage();
      return;
    }

    const validationError = this.validateFile(this.selectedFile);
    if (validationError) {
      this.uploadError = validationError;
      return;
    }

    const draftPayload = this.buildDocumentPayload({
      name: this.selectedFile.name,
      path: '',
      attachmenttype: this.selectedFile.type
    });

    if (this.hasDuplicate(draftPayload)) {
      const allowOverride = this.evaluateBoolean(this.config.duplicate.allowOverrideExpression || 'false', false);
      this.pendingSourceUploadModal = modal;
      this.openDuplicateModal(allowOverride ? this.duplicateStaffModalTemplate : this.duplicateApplicantModalTemplate);
      return;
    }

    await this.performUpload(modal);
  }

  async onDuplicateProceed(): Promise<void> {
    const sourceModal = this.pendingSourceUploadModal;
    if (this.duplicateModalRef) {
      this.duplicateModalRef.close();
      this.duplicateModalRef = null;
    }
    this.pendingSourceUploadModal = null;
    if (!sourceModal) {
      return;
    }
    await this.performUpload(sourceModal);
  }

  onDuplicateCancel(modal?: any): void {
    if (modal?.close) {
      modal.close();
    }
    if (this.duplicateModalRef) {
      this.duplicateModalRef.close();
      this.duplicateModalRef = null;
    }
    this.pendingSourceUploadModal = null;
  }

  requestCloseUploadModal(modal: NgbModalRef): void {
    if (!this.hasPendingUploadChanges()) {
      modal.dismiss();
      return;
    }
    this.pendingSourceUploadModal = modal;
    const confirmModalRef = this.modalService.open(ModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    confirmModalRef.componentInstance.modalConfig = getCancelFileUploadModalConfig();
    confirmModalRef.result
      .then((result: unknown) => {
        if (isCancelFileUploadConfirmed(result)) {
          this.discardUploadChanges();
        }
      })
      .catch(() => {
        /* dismissed - keep upload modal open */
      });
  }

  discardUploadChanges(): void {
    const sourceModal = this.pendingSourceUploadModal;
    this.pendingSourceUploadModal = null;
    this.resetUploadDraftState();
    sourceModal?.dismiss();
  }

  preview(row: SupportingDocumentRow, template: TemplateRef<any>): void {
    const url = row?.path || row?.file_hash;
    if (!url) {
      this.alertService.error('Preview is not available.');
      return;
    }
    this.previewDocument = row;
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.modalService.open(template, { size: 'xl', centered: true });
  }

  download(row: SupportingDocumentRow): void {
    const url = row?.path || row?.file_hash;
    if (!url) {
      this.alertService.error('Download is not available.');
      return;
    }
    this.http.get(url, { responseType: 'blob' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: Blob) => {
          const blobUrl = window.URL.createObjectURL(response);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = row.file_name || 'document';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        },
        error: (error: any) => {
          console.error(error);
          this.alertService.error('Unable to download document.');
        }
      });
  }

  isImage(row: SupportingDocumentRow | null): boolean {
    const name = String(row?.file_name || '').toLowerCase();
    return ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'].some((ext: string) => name.endsWith(ext));
  }

  openDeleteModal(template: TemplateRef<any>, row: SupportingDocumentRow): void {
    if (!row) {
      return;
    }
    this.deleteTargetRow = row;
    this.deleteModalRef = this.modalService.open(template, {
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      centered: true,
      windowClass: 'add-appointment-details'
    });
    this.deleteModalRef.result.finally(() => {
      this.deleteModalRef = null;
      this.deleteTargetRow = null;
    });
  }

  confirmDelete(modal?: NgbModalRef): void {
    const row = this.deleteTargetRow;
    modal?.close();
    if (row) {
      void this.deleteDocument(row);
    }
  }

  cancelDelete(modal?: NgbModalRef): void {
    modal?.dismiss();
  }

  async deleteDocument(row: SupportingDocumentRow): Promise<void> {
    if (!row) {
      return;
    }
    try {
      await this.persistDelete(row);
      this.clearPlaceholderTypeSelectionForDeletedRow(row);
      this.rawDocuments = this.rawDocuments.map((item: SupportingDocumentRow) => this.isSameRow(item, row)
        ? { ...item, status: this.config.statuses.deleted, is_active: false }
        : item
      );
      this.rebuildDerivedState();
      this.alertService.success('Document deleted successfully.');
    } catch (error) {
      console.error(error);
      this.alertService.error('Unable to delete document.');
    }
  }

  private clearPlaceholderTypeSelectionForDeletedRow(row: SupportingDocumentRow): void {
    if (!row) {
      return;
    }

    if (row._placeholderKey) {
      delete this.placeholderTypeSelections[row._placeholderKey];
    }

    const deletedCategory = this.normalizeValue(row.cd_document_category);
    const deletedType = this.normalizeValue(row.cd_document_type);
    const deletedSection = this.normalizeValue(row.section_name || this.resolveSectionName(row) || '');
    const deletedRuleId = row.rule_id ? String(row.rule_id) : '';

    Object.entries(this.placeholderTypeSelections).forEach(([key, value]: [string, string]) => {
      const normalizedKey = this.normalizeValue(key);
      const normalizedValue = this.normalizeValue(value);
      const categoryMatches = !deletedCategory || normalizedKey.includes(deletedCategory);
      const sectionMatches = !deletedSection || normalizedKey.includes(deletedSection);
      const typeMatches = !deletedType || normalizedValue === deletedType;
      const ruleMatches = !!deletedRuleId && key.startsWith(`db-${deletedRuleId}-`);

      if ((typeMatches && categoryMatches && sectionMatches) || ruleMatches) {
        delete this.placeholderTypeSelections[key];
      }
    });
  }

  markValid(row: SupportingDocumentRow): void {
    this.validTargetRow = row;
    this.confirmValidModalRef = this.modalService.open(this.confirmValidModalTemplate, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });
    this.confirmValidModalRef.result.finally(() => {
      this.confirmValidModalRef = null;
      this.validTargetRow = null;
    });
  }

  confirmValid(modal?: NgbModalRef): void {
    const row = this.validTargetRow;
    modal?.close();
    this.updateRowStatus(row, this.config.statuses.validated);
  }

  cancelValid(modal?: NgbModalRef): void {
    modal?.dismiss();
  }

  openDeficientModal(template: TemplateRef<any>, row: SupportingDocumentRow): void {
    this.deficiencyTarget = row;
    this.deficiencyNotes = row?.document_notes || '';
    this.deficiencyError = '';
    this.modalService.open(template, { size: 'md', centered: true, backdrop: 'static' });
  }
  openAddressedModal(template: TemplateRef<any>, row: SupportingDocumentRow): void {
    this.deficiencyTarget = row;
    this.deficiencyNotes = row?.document_notes || '';
    this.deficiencyError = '';
    this.modalService.open(template, { size: 'md', centered: true, backdrop: 'static' });
  }
  openViewDeficiencyModal(template: TemplateRef<any>, row: SupportingDocumentRow): void {
    this.deficiencyTarget = row;
    this.deficiencyNotes = row?.document_notes || '';
    this.deficiencyError = '';
    this.modalService.open(template, { size: 'md', centered: true, backdrop: 'static' });
  }
  onMarkAsAddressedItem(modal: NgbModalRef) {
    this.updateRowStatus(this.deficiencyTarget, this.config.statuses.addressed, this.deficiencyNotes.trim());
    modal.close();
  }
  confirmDeficient(modal: NgbModalRef): void {
    if (!String(this.deficiencyNotes || '').trim()) {
      this.deficiencyError = 'Deficiency notes are required.';
      return;
    }
    this.updateRowStatus(this.deficiencyTarget, this.config.statuses.deficient, this.deficiencyNotes.trim());
    modal.close();
  }

  private async updateRowStatus(row: SupportingDocumentRow | null, status: string, notes = ''): Promise<void> {
    if (!row) {
      return;
    }
    try {
      const updated = await this.persistStatus(row, status, notes);
      this.rawDocuments = this.applyStatusToRows(row, status, notes, updated);
      this.rebuildDerivedState();
      this.alertService.success(`Document marked as ${status}.`);
    } catch (error) {
      console.error(error);
      this.alertService.error(`Unable to update document status to ${status}.`);
    }
  }

  private rebuildConfig(): void {
    this.config = this.configService.parse(this.buildConfigInputs(), {
      readOnlyExpression: this.readOnlyCondition,
      showArchivedExpression: this.showArchived
    });
    this.applyResolvedDocumentStatuses();
    this.rebuildDerivedState();
  }

  private buildConfigInputs(): Record<string, any> {
    return {
      value: this.value,
      disabled: this.disabled,
      name: this.name,
      buttonLabel: this.buttonLabel,
      formId: this.formId,
      gridId: this.gridId,
      query: this.query,
      fields: this.fields,
      configJson: this.configJson,
      readOnlyCondition: this.readOnlyCondition,
      showArchived: this.showArchived,
      allowedExtensions: this.allowedExtensions,
      maxFileSizeMb: this.maxFileSizeMb,
      requireCategory: this.requireCategory,
      requireType: this.requireType,
      archiveEnabledExpression: this.archiveEnabledExpression,
      archivedStatuses: this.archivedStatuses,
      duplicateMatchOn: this.duplicateMatchOn,
      duplicateAllowOverrideExpression: this.duplicateAllowOverrideExpression,
      duplicateBlockMessageStaff: this.duplicateBlockMessageStaff,
      duplicateBlockMessageApplicant: this.duplicateBlockMessageApplicant,
      uploadActionExpression: this.uploadActionExpression,
      viewActionExpression: this.viewActionExpression,
      downloadActionExpression: this.downloadActionExpression,
      deleteActionExpression: this.deleteActionExpression,
      replaceActionExpression: this.replaceActionExpression,
      markValidActionExpression: this.markValidActionExpression,
      markDeficientActionExpression: this.markDeficientActionExpression,
      markAddressedActionExpression: this.markAddressedActionExpression,
      viewDeficiencyActionExpression: this.viewDeficiencyActionExpression,
      categoriesConfig: this.categoriesConfig,
      documentTypesConfig: this.documentTypesConfig,
      requirementsConfig: this.requirementsConfig,
      rulesEndpoint: this.rulesEndpoint,
      stepperId: this.stepperId,
      pageId: this.pageId,
      panelKey: this.panelKey,
      listEndpoint: this.listEndpoint,
      saveEndpoint: this.saveEndpoint,
      updateStatusEndpoint: this.updateStatusEndpoint,
      deleteEndpoint: this.deleteEndpoint,
      replaceEndpoint: this.replaceEndpoint,
      syncSaveEndpoint: this.syncSaveEndpoint,
      syncDeleteEndpoint: this.syncDeleteEndpoint,
      pendingReviewStatus: this.pendingReviewStatus,
      validatedStatus: this.validatedStatus,
      deficientStatus: this.deficientStatus,
      archivedStatus: this.archivedStatus,
      deletedStatus: this.deletedStatus,
      isDynamicTypeDropdownNeeded: this.isDynamicTypeDropdownNeeded,
      dynamicDocumentSourceGridKey: this.dynamicDocumentSourceGridKey,
      dynamicDocumentSourceRecordKeyField: this.dynamicDocumentSourceRecordKeyField,
      dynamicDocumentSourceTypeField: this.dynamicDocumentSourceTypeField,
      dynamicDocumentSourceTypeLookupRequestType: this.dynamicDocumentSourceTypeLookupRequestType,
      showOnlyAvailableDocuments: this.showOnlyAvailableDocuments,
      showAdditionalUploadInViewOnly: this.showAdditionalUploadInViewOnly,
      showDeleteButtonInViewOnly: this.showDeleteButtonInViewOnly,
      hideUploadConditionEnabled: this.hideUploadConditionEnabled,
      hideUploadConditionExpression: this.hideUploadConditionExpression,
      isAdditionalDynamicTypeNeeded: this.isAdditionalDynamicTypeNeeded,
      additionalDynamicTypeSourceGridKey: this.additionalDynamicTypeSourceGridKey,
      additionalDynamicTypeSourceFieldName: this.additionalDynamicTypeSourceFieldName,
      additionalDynamicTypeCategoryFieldName: this.additionalDynamicTypeCategoryFieldName,
      showOnlyConditionEnabled: this.showOnlyConditionEnabled,
      showOnlyConditionExpression: this.showOnlyConditionExpression,
      showOnlyCategoryConditionEnabled: this.showOnlyCategoryConditionEnabled,
      showOnlyCategoryName: this.showOnlyCategoryName,
      showOnlyCategoryConditionExpression: this.showOnlyCategoryConditionExpression,
      caseDocEnabled: this.caseDocEnabled
    };
  }

private loadRules(): void {
    // View-only mode normally skips rules. But when Case Document Mode is also
    // enabled we still load the rules so the grid can decide visibility from
    // matched rules and filter the upload modal categories/types accordingly.
    if (this.isShowOnlyAvailableDocumentsMode() && !this.isCaseDocMode()) {
      this.lastRulesRequestKey = '';
      this.dbRules = [];
      this.rebuildDerivedState();
      return;
    }
    const endpoint = this.resolveEndpoint(this.config?.api?.rulesEndpoint || this.rulesEndpoint || '');
    const query = {
      stepperId: this.config?.ruleScope?.stepperId || this.stepperId || this.resolveEffectiveFormId() || '',
      pageId: this.config?.ruleScope?.pageId || this.pageId || '',
      panelKey: this.config?.ruleScope?.panelKey || this.panelKey || ''
    };
    const requestKey = JSON.stringify({ endpoint, query });

    if (!endpoint && !query.stepperId && !query.pageId && !query.panelKey) {
      this.lastRulesRequestKey = '';
      this.dbRules = [];
      this.rebuildDerivedState();
      return;
    }
    if (requestKey === this.lastRulesRequestKey) {
      return;
    }
    this.lastRulesRequestKey = requestKey;

    this.supportingDocumentRuleConfigService.listRules(query, endpoint || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows: SupportingDocumentRuleDefinition[]) => {
          this.dbRules = rows.filter((row: SupportingDocumentRuleDefinition) => row?.is_active !== false);
          this.rebuildDerivedState();
        },
        error: (error) => {
          console.error('Failed to load supporting document rules', error);
          this.lastRulesRequestKey = '';
          this.dbRules = [];
          this.rebuildDerivedState();
        }
      });
  }

  private loadInitialDocuments(): void {
    const incomingRows = this.normalizeIncomingDocuments(this.value);
    // In view-only mode documents are loaded from the API (by application +
    // section), not from the form value. Seeding from an empty incoming value
    // here (which can happen when a read-only form emits its default empty
    // value, or when this request is deduped/skipped below) would wipe the
    // already-loaded documents and leave the grid empty. Preserve them.
    if (!(this.isShowOnlyAvailableDocumentsMode() && !incomingRows.length && this.rawDocuments.length)) {
      this.rawDocuments = incomingRows;
    }const responseId = this.resolveApplicationId();
    const effectiveFormId = this.resolveEffectiveFormId();

    // if (this.rawDocuments.length || !responseId || !effectiveFormId) {
    //   this.rebuildDerivedState();
    //   return;
    // }

    const viewOnlyMode = this.isShowOnlyAvailableDocumentsMode();
    const sectionName = this.config?.ruleScope?.panelKey || this.panelKey || '';


    const query = viewOnlyMode
      ? {
          stepperId: '',
          pageId: '',
          panelKey: sectionName,
          applicationId: responseId || ''
        }
      : {
          stepperId: this.config?.ruleScope?.stepperId || this.stepperId || this.resolveEffectiveFormId() || '',
          pageId: this.config?.ruleScope?.pageId || this.pageId || '',
          panelKey: this.config?.ruleScope?.panelKey || this.panelKey || '',
          applicationId: responseId || ''
        };
    const listEndpoint = this.resolveListEndpoint();
    const params = new URLSearchParams();
    if (!viewOnlyMode && query?.pageId !== undefined && query?.pageId !== null && String(query.pageId).trim() !== '') {
      params.set('formid', String(query.pageId));
    }
    if (query?.panelKey !== undefined && query?.panelKey !== null && String(query.panelKey).trim() !== '') {
      params.set('section_name', String(query.panelKey));
    }
    if (query?.applicationId !== undefined && query?.applicationId !== null && String(query.applicationId).trim() !== '') {
      params.set('application_id', String(query.applicationId));
    }
    params.set('includeInactive', 'true');
    const queryString = params.toString();
    const getListEndPoint = queryString ? `${listEndpoint}?${queryString}` : '';
    const requestKey = JSON.stringify({ listEndpoint, queryString, responseId, effectiveFormId, viewOnlyMode });
    const request$ = listEndpoint
      ? this.httpService.get(getListEndPoint)
      : this.pageBuilderService.getDocumentsHistory(responseId, effectiveFormId);

    const requiredParamsPresent = viewOnlyMode
      ? !!params.get('application_id') && !!params.get('section_name')
      : !!params.get('application_id') && !!params.get('formid') && !!params.get('section_name');


    if (!requiredParamsPresent) {
      this.lastDocumentsRequestKey = '';
      this.rebuildDerivedState();
      return;
    }
    if (requestKey === this.lastDocumentsRequestKey) {
      return;
    }
    this.lastDocumentsRequestKey = requestKey;

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const rows = Array.isArray(response?.data?.data)
            ? response.data.data
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response?.rows)
                ? response.rows
                : Array.isArray(response)
                  ? response
                  : [];
          this.rawDocuments = this.normalizeIncomingDocuments(rows);
          this.rememberSyncedTargetRowsLoadedForSection(sectionName, this.rawDocuments);
          this.rebuildDerivedState();
        },
        error: (err: any) => {
          // eslint-disable-next-line no-console
          console.error('[SupportingDocumentGrid] documents request failed', err);
          this.lastDocumentsRequestKey = '';
          this.rebuildDerivedState();
        }
      });
  }

  private extractDocumentRows(response: any): any[] {
    return Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.rows)
          ? response.rows
          : Array.isArray(response)
            ? response
            : [];
  }

  private buildDocumentListRequest(): { request$: any; requiredParamsPresent: boolean } {
    const responseId = this.resolveApplicationId();
    const effectiveFormId = this.resolveEffectiveFormId();
    const viewOnlyMode = this.isShowOnlyAvailableDocumentsMode();
    const sectionName = this.config?.ruleScope?.panelKey || this.panelKey || '';
    const query = viewOnlyMode
      ? {
          pageId: '',
          panelKey: sectionName,
          applicationId: responseId || ''
        }
      : {
          pageId: this.config?.ruleScope?.pageId || this.pageId || '',
          panelKey: this.config?.ruleScope?.panelKey || this.panelKey || '',
          applicationId: responseId || ''
        };
    const listEndpoint = this.resolveListEndpoint();
    const params = new URLSearchParams();
    if (!viewOnlyMode && query?.pageId !== undefined && query?.pageId !== null && String(query.pageId).trim() !== '') {
      params.set('formid', String(query.pageId));
    }
    if (query?.panelKey !== undefined && query?.panelKey !== null && String(query.panelKey).trim() !== '') {
      params.set('section_name', String(query.panelKey));
    }
    if (query?.applicationId !== undefined && query?.applicationId !== null && String(query.applicationId).trim() !== '') {
      params.set('application_id', String(query.applicationId));
    }
    params.set('includeInactive', 'true');
    const queryString = params.toString();
    const getListEndPoint = queryString ? `${listEndpoint}?${queryString}` : '';
    const request$ = listEndpoint
      ? this.httpService.get(getListEndPoint)
      : this.pageBuilderService.getDocumentsHistory(responseId, effectiveFormId);
    const requiredParamsPresent = viewOnlyMode
      ? !!params.get('application_id') && !!params.get('section_name')
      : !!params.get('application_id') && !!params.get('formid') && !!params.get('section_name');

    return { request$, requiredParamsPresent };
  }

  private reloadDocumentsForCurrentSection(): void {
    this.lastDocumentsRequestKey = '';
    this.loadInitialDocuments();
  }

  private onSyncDocumentsChanged(event: Event): void {
    const detail = (event as CustomEvent)?.detail || {};
    const targetSections = Array.isArray(detail.targetSections) ? detail.targetSections : [];
    const sourceDocumentId = String(detail.sourceDocumentId || '').trim();
    const currentSectionName = this.getCurrentSectionName();
    const shouldReload = !targetSections.length
      || targetSections.some((sectionName: any) => this.normalizeValue(sectionName) === this.normalizeValue(currentSectionName));

    if (shouldReload) {
      if (sourceDocumentId) {
        this.rememberSyncedSourceOrder(currentSectionName, sourceDocumentId);
        this.pendingSyncedTargetRowsBeforeReload[this.normalizeValue(currentSectionName)] = this.getCurrentSectionDocumentKeys();
      }
      this.reloadDocumentsForCurrentSection();
    }
  }

  private rememberSyncedSourceOrder(sectionName: string, sourceDocumentId: string): void {
    const key = this.normalizeValue(sectionName);
    if (!key || !sourceDocumentId) {
      return;
    }
    const existing = this.syncedSourceOrderBySection[key] || [];
    if (!existing.includes(sourceDocumentId)) {
      this.syncedSourceOrderBySection[key] = [...existing, sourceDocumentId];
    }
  }

  private getCurrentSectionDocumentKeys(): string[] {
    const currentSectionName = this.getCurrentSectionName();
    return (this.rawDocuments || [])
      .filter((row: SupportingDocumentRow) =>
        this.normalizeValue(row.section_name || this.resolveSectionName(row) || '') === this.normalizeValue(currentSectionName)
      )
      .map((row: SupportingDocumentRow) => this.getSyncedTargetRowKey(row))
      .filter((key: string) => !!key);
  }

  private rememberSyncedTargetRowsLoadedForSection(sectionName: string, rows: SupportingDocumentRow[]): void {
    const sectionKey = this.normalizeValue(sectionName || this.getCurrentSectionName());
    const beforeReload = this.pendingSyncedTargetRowsBeforeReload[sectionKey];
    if (!beforeReload) {
      return;
    }
    if (!this.getAllConfiguredSyncDocuments().length) {
      return;
    }

    const existing = this.syncedTargetRowsBySection[sectionKey] || [];
    const nextKeys = (rows || [])
      .filter((row: SupportingDocumentRow) =>
        this.hasUploadedFile(row)
        && this.matchesConfiguredSyncTarget(row)
        && !beforeReload.includes(this.getSyncedTargetRowKey(row))
      )
      .map((row: SupportingDocumentRow) => this.getSyncedTargetRowKey(row))
      .filter((key: string) => !!key && !existing.includes(key));

    if (nextKeys.length) {
      this.syncedTargetRowsBySection[sectionKey] = [...existing, ...nextKeys];
    }
    delete this.pendingSyncedTargetRowsBeforeReload[sectionKey];
  }

  private normalizeIncomingDocuments(value: any): SupportingDocumentRow[] {
    const rows = Array.isArray(value)
      ? value
      : Array.isArray(value?.documents)
        ? value.documents
        : Array.isArray(value?.value)
          ? value.value
          : [];

    if (!Array.isArray(rows)) {
      return [];
    }
    return rows
      .map((item: any) => {
         const additionalInfo = this.parseAdditionalInfo(item?.additional_info ?? item?.additionalInfo);
         const mirroredFromDocumentId = item?.mirrored_from_document_id
          ?? item?.mirroredFromDocumentId
          ?? additionalInfo?.mirrored_from_document_id
          ?? additionalInfo?.mirroredFromDocumentId
          ?? null;
         const isSystemGenerated = item?.is_system_generated
          ?? item?.isSystemGenerated
          ?? additionalInfo?.is_system_generated
          ?? additionalInfo?.isSystemGenerated
          ?? false;
         return {
          ...item,
          additional_info: additionalInfo,
          additionalInfo,
          document_id: item?.document_id ?? item?.id ?? item?.primary_id ?? null,
          file_hash: item?.file_hash || item?.path || '',
          path: item?.path || item?.file_hash || '',
          is_active: item?.is_active !== false,
          created_by_name: item?.created_by_name || item?.created_by || item?.uploaded_by || '',
          uploaded_at: item?.uploaded_at || item?.created_date || item?.createdat || '',
          section_name: item?.section_name || item?.sectionName || '',
          mirrored_from_document_id: mirroredFromDocumentId,
          mirroredFromDocumentId,
          is_system_generated: isSystemGenerated,
          is_profile_document: !!item?.profile_document_id,
          isSystemGenerated,
          dynamic_source_key: item?.dynamic_source_key || item?.dynamicSourceKey || item?._dynamicSourceKey || '',
          dynamic_rule_id: item?.dynamic_rule_id || item?.dynamicRuleId || item?._dynamicRuleId || '',
          dynamic_type_lookup_request_type: item?.dynamic_type_lookup_request_type || item?.dynamicTypeLookupRequestType || item?._dynamicTypeLookupRequestType || '',
          _dynamicSourceKey: item?._dynamicSourceKey || item?.dynamic_source_key || item?.dynamicSourceKey || '',
          _dynamicRuleId: item?._dynamicRuleId || item?.dynamic_rule_id || item?.dynamicRuleId || '',
          _dynamicTypeLookupRequestType: item?._dynamicTypeLookupRequestType || item?.dynamic_type_lookup_request_type || item?.dynamicTypeLookupRequestType || '',
          is_new: item?.is_new ?? false,
          is_read_only: item?.is_read_only ?? false,
          is_highlighted: item?.is_highlighted ?? false,
          is_highlighted_update: item?.is_highlighted_update ?? false
        };
      })
      .filter((item: SupportingDocumentRow) => item.is_active !== false || item.status !== this.config.statuses.deleted);
  }

  private rebuildDerivedState(): void {
    this.readOnly = this.evaluateFeatureFlag(this.config.readOnlyExpression || '', false, ['readOnly', 'readonly']);
    this.archiveEnabled = this.evaluateFeatureFlag(this.config.archive.enabledExpression || '', false, ['showArchive', 'showarchive', 'archiveEnabled']);
    const currentSectionName = this.getCurrentSectionName();

    const normalizedRows = this.rawDocuments
      .filter((row: SupportingDocumentRow) => this.normalizeStatus(row.status) !== this.normalizeStatus(this.config.statuses.deleted))
      .map((row: SupportingDocumentRow) => ({
        ...row,
        section_name: row.section_name || this.resolveSectionName(row)
      }))
      .filter((row: SupportingDocumentRow) => this.shouldIncludeRowForSection(row, currentSectionName));

    const activeSourceRows = normalizedRows.filter((row: SupportingDocumentRow) => !this.isArchived(row));
    const placeholders = this.buildPlaceholderRows(activeSourceRows);
    const dependentPlaceholders = this.buildDependentPlaceholderRows([...activeSourceRows, ...placeholders]);
    const allRows = [...normalizedRows, ...placeholders, ...dependentPlaceholders];

    this.activeDocuments = this.sortRowsForDisplay(allRows.filter((row: SupportingDocumentRow) =>
      !this.isArchived(row) && this.isCategoryAllowedForCurrentUser(row.cd_document_category || '')
    ));
    this.archivedDocuments = this.sortRowsForDisplay(allRows.filter((row: SupportingDocumentRow) =>
      this.isArchived(row) && this.isCategoryAllowedForCurrentUser(row.cd_document_category || '')
    ));
    if (!this.archiveEnabled) {
      this.activeTab = 'active';
    }
    this.refreshVisibleDocuments();
    this.syncNavigationValidationHandlers();
    this.emitValue();
    this.cdr.detectChanges();
  }

  private buildPlaceholderRows(rows: SupportingDocumentRow[]): SupportingDocumentRow[] {
    if (this.isShowOnlyAvailableDocumentsMode()) {
      // In view-only mode we don't render placeholder rows. However, when Case
      // Document Mode is enabled we still evaluate the DB rules to collect the
      // case-doc upload requirements (categories/types shown in the additional
      // upload modal and used to drive visibility) without adding any rows.
      if (this.isCaseDocMode() && this.dbRules.length) {
        return this.buildPlaceholderRowsFromDbRules(rows);
      }
      this.caseDocUploadRequirements = [];
      this.caseDocHasAllCategoryRequirement = false;
      return [];
    }
    if (this.dbRules.length) {
      return this.buildPlaceholderRowsFromDbRules(rows);
    }

    return this.buildPlaceholderRowsFromConfig(rows);
  }

  private sortRowsForDisplay(rows: SupportingDocumentRow[]): SupportingDocumentRow[] {
    const sourceOrderByDocumentId = this.buildDocumentOrderMap();
    const sourceOrderByDynamicKey = this.buildDynamicSourceOrderMap();

    return [...(rows || [])]
      .map((row: SupportingDocumentRow, index: number) => ({
        row,
        index,
        rank: this.getDisplaySortRank(row, sourceOrderByDocumentId, sourceOrderByDynamicKey)
      }))
      .sort((left, right) => {
        if (left.rank !== right.rank) {
          return left.rank - right.rank;
        }
        return left.index - right.index;
      })
      .map((item) => item.row);
  }

  private buildDynamicSourceOrderMap(): Map<string, number> {
    const sourceOrderByDynamicKey = new Map<string, number>();
    if (!this.hasDocumentSourceGrid()) {
      return sourceOrderByDynamicKey;
    }
    this.buildDynamicSourceRecordEntries().forEach((entry, index: number) => {
      if (entry?.sourceKey && !sourceOrderByDynamicKey.has(entry.sourceKey)) {
        sourceOrderByDynamicKey.set(entry.sourceKey, index);
      }
    });
    return sourceOrderByDynamicKey;
  }

  private buildDocumentOrderMap(): Map<string, number> {
    const sourceOrderByDocumentId = new Map<string, number>();
    const currentSectionKey = this.normalizeValue(this.getCurrentSectionName());
    (this.syncedSourceOrderBySection[currentSectionKey] || []).forEach((documentId: string, index: number) => {
      if (documentId && !sourceOrderByDocumentId.has(documentId)) {
        sourceOrderByDocumentId.set(documentId, index);
      }
    });

    (this.rawDocuments || []).forEach((row: SupportingDocumentRow, index: number) => {
      const documentId = String(row?.document_id || '').trim();
      if (documentId && !sourceOrderByDocumentId.has(documentId)) {
        sourceOrderByDocumentId.set(documentId, index);
      }
    });

    this.appendDocumentOrderFromValue(this.formData?.data || this.formData || {}, sourceOrderByDocumentId, new Set<any>());
    return sourceOrderByDocumentId;
  }

  private appendDocumentOrderFromValue(value: any, sourceOrderByDocumentId: Map<string, number>, visited: Set<any>): void {
    if (!value || typeof value !== 'object') {
      return;
    }
    if (visited.has(value)) {
      return;
    }
    visited.add(value);

    if (Array.isArray(value)) {
      const documentRows = value.filter((item: any) => item && typeof item === 'object' && (item.document_id || item.id || item.primary_id));
      if (documentRows.length) {
        documentRows.forEach((row: any, index: number) => {
          const documentId = String(row.document_id ?? row.id ?? row.primary_id ?? '').trim();
          if (documentId && !sourceOrderByDocumentId.has(documentId)) {
            sourceOrderByDocumentId.set(documentId, index);
          }
        });
      }
      value.forEach((item: any) => this.appendDocumentOrderFromValue(item, sourceOrderByDocumentId, visited));
      return;
    }

    ['activeDocuments', 'documents', 'value'].forEach((key: string) => {
      if (Array.isArray(value[key])) {
        this.appendDocumentOrderFromValue(value[key], sourceOrderByDocumentId, visited);
      }
    });
    Object.keys(value).forEach((key: string) => {
      if (value[key] && typeof value[key] === 'object') {
        this.appendDocumentOrderFromValue(value[key], sourceOrderByDocumentId, visited);
      }
    });
  }

  private getDisplaySortRank(
    row: SupportingDocumentRow,
    sourceOrderByDocumentId: Map<string, number>,
    sourceOrderByDynamicKey?: Map<string, number>
  ): number {
    // Rows tied to a dynamic source grid record should follow the source
    // grid's row order, regardless of upload time or DB insertion order.
    const dynamicSourceKey = this.getRowDynamicSourceKey(row);
    if (dynamicSourceKey && sourceOrderByDynamicKey?.has(dynamicSourceKey)) {
      return sourceOrderByDynamicKey.get(dynamicSourceKey) as number;
    }

    const mirroredFromDocumentId = String(row?.mirrored_from_document_id ?? row?.mirroredFromDocumentId ?? '').trim();
    if (mirroredFromDocumentId && sourceOrderByDocumentId.has(mirroredFromDocumentId)) {
      return sourceOrderByDocumentId.get(mirroredFromDocumentId) as number;
    }

    const numericId = Number(mirroredFromDocumentId);
    if (mirroredFromDocumentId && Number.isFinite(numericId)) {
      return numericId;
    }

    if (this.isSystemManagedDocument(row)) {
      return this.getRowSortTime(row);
    }

    return Number.MAX_SAFE_INTEGER;
  }

  private buildDependentPlaceholderRows(rows: SupportingDocumentRow[]): SupportingDocumentRow[] {
    const placeholders: SupportingDocumentRow[] = [];
    const createdPlaceholderKeys = new Set<string>();

    (rows || []).forEach((sourceRow: SupportingDocumentRow) => {
      if (this.isSystemManagedDocument(sourceRow)) {
        return;
      }
      if (!String(sourceRow?.cd_document_category || '').trim() || !String(sourceRow?.cd_document_type || '').trim()) {
        return;
      }

      const dependencies = this.getDependentRequirementsForRow(sourceRow);
      dependencies.forEach((dependency: SupportingDocumentDependentRequirement, dependencyIndex: number) => {
        const target = this.resolveDependentRequirementTarget(dependency);
        const category = target.category;
        const typeValue = target.type;
        if (!category) {
          return;
        }

        const sectionName = String(dependency.sectionName ?? sourceRow.section_name ?? this.resolveSectionName(sourceRow) ?? '').trim();
        const placeholderCount = Math.max(1, Number(dependency.placeholderCount || 1));

        for (let index = 0; index < placeholderCount; index += 1) {
          const placeholderKey = [
            'dependent',
            sourceRow.cd_document_category || '',
            sourceRow.cd_document_type || '',
            category,
            typeValue,
            sectionName,
            dependencyIndex,
            index
          ].map((item: any) => this.normalizeValue(item)).join('-');

          if (createdPlaceholderKeys.has(placeholderKey)) {
            return;
          }

          const matchedUploadedRow = rows.find((row: SupportingDocumentRow) =>
            this.hasUploadedFile(row) && this.rowMatchesRequirement(row, category, typeValue, sectionName)
          );
          const matchedPlaceholderRow = placeholders.find((row: SupportingDocumentRow) =>
            this.rowMatchesRequirement(row, category, typeValue, sectionName)
          );
          const alreadyRepresented = !!matchedUploadedRow || !!matchedPlaceholderRow;

          if (alreadyRepresented) {
            if (matchedUploadedRow) {
              matchedUploadedRow._placeholderKey = matchedUploadedRow._placeholderKey || placeholderKey;
              matchedUploadedRow._lockType = matchedUploadedRow._lockType || (dependency.lockType !== false && !!typeValue);
            }
            createdPlaceholderKeys.add(placeholderKey);
            return;
          }

          createdPlaceholderKeys.add(placeholderKey);
          placeholders.push({
            cd_document_category: category,
            cd_document_type: typeValue,
            section_name: sectionName,
            status: '',
            file_name: '',
            file_hash: '',
            isPlaceholder: true,
            _lockType: dependency.lockType !== false && !!typeValue,
            _placeholderKey: placeholderKey
          });
        }
      });
    });

    return placeholders;
  }

  private getDependentRequirementsForRow(row: SupportingDocumentRow): SupportingDocumentDependentRequirement[] {
    const typeOption = this.resolveTypeOption(
      row.cd_document_category || '',
      row.cd_document_type || '',
      this.getRowDynamicTypeLookupRequestType(row)
    );
    const optionalData = typeOption?.optionaldata;
    if (!optionalData || typeof optionalData !== 'object') {
      return [];
    }

    const dependencySource = optionalData.requiredDocuments
      || optionalData.requiresDocuments
      || optionalData.dependentDocuments
      || optionalData.dependentRequiredDocuments
      || optionalData.supportingDocumentDependencies
      || optionalData.documentDependencies
      || optionalData.requiredDocument
      || optionalData.requiresDocument
      || optionalData.dependentDocument
      || optionalData.dependentRequiredDocument;

    const dependencyRows = Array.isArray(dependencySource)
      ? dependencySource
      : dependencySource
        ? [dependencySource]
        : [];

    return dependencyRows
      .map((item: any) => this.normalizeDependentRequirement(item))
      .filter((item: SupportingDocumentDependentRequirement | null): item is SupportingDocumentDependentRequirement => !!item);
  }

  private normalizeDependentRequirement(item: any): SupportingDocumentDependentRequirement | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const category = String(
      item.category
      ?? item.categoryId
      ?? item.category_id
      ?? item.cd_document_category
      ?? item.documentCategory
      ?? item.document_category
      ?? item.categoryKey
      ?? item.categoryLookupId
      ?? item.category_lookup_id
      ?? ''
    ).trim();
    const type = String(
      item.type
      ?? item.typeId
      ?? item.type_id
      ?? item.cd_document_type
      ?? item.documentType
      ?? item.document_type
      ?? item.typeKey
      ?? item.lookupId
      ?? item.lookup_id
      ?? ''
    ).trim();

    if (!category && !type) {
      return null;
    }

    return {
      category,
      type,
      sectionName: item.sectionName ?? item.section_name,
      lockType: item.lockType ?? item.lock_type,
      placeholderCount: item.placeholderCount ?? item.placeholder_count
    };
  }

  private resolveDependentRequirementTarget(dependency: SupportingDocumentDependentRequirement): { category: string; type: string } {
    const configuredCategory = String(dependency.category || '').trim();
    const configuredType = String(dependency.type || '').trim();
    if (configuredCategory) {
      const categoryOption = this.resolveCategoryOption(configuredCategory);
      const typeOption = this.resolveTypeOption(categoryOption?.key || configuredCategory, configuredType);
      return {
        category: categoryOption?.key || configuredCategory,
        type: typeOption?.key || configuredType
      };
    }

    const typeOption = this.resolveTypeOption('', configuredType);
    const parentId = String(typeOption?.parentId || '').trim();
    const categoryOption = (this.lookupCategories || []).find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.lookupId, parentId)
      || (item.lookup || []).some((child: SupportingDocumentGridOption) =>
        this.isSameLookupValue(child.lookupId, typeOption?.lookupId)
        || this.isSameLookupValue(child.key, configuredType)
        || this.isSameLookupValue(child.value, configuredType)
      )
    );

    return {
      category: categoryOption?.key || '',
      type: typeOption?.key || configuredType
    };
  }

  private buildPlaceholderRowsFromDbRules(rows: SupportingDocumentRow[]): SupportingDocumentRow[] {
    // Reset the case-document upload requirements each rebuild. When Case
    // Document Mode is on, matched rules are collected here instead of being
    // rendered as placeholder rows in the grid.
    this.caseDocUploadRequirements = [];
    this.caseDocHasAllCategoryRequirement = false;
    const placeholders: SupportingDocumentRow[] = [];
    const existingRows = rows.filter((row: SupportingDocumentRow) => !row.isPlaceholder);
    const activeRules = [...this.dbRules]
      .filter((rule: SupportingDocumentRuleDefinition) => this.ruleMatchesScope(rule))
      .sort((left: SupportingDocumentRuleDefinition, right: SupportingDocumentRuleDefinition) =>
        Number(left.sort_order || 0) - Number(right.sort_order || 0)
      );

    if (this.hasDocumentSourceGrid() && this.dynamicSourceRecords.length) {
      return this.buildSourceGridPlaceholderRowsFromDbRules(rows, activeRules);
    }

    activeRules.forEach((rule: SupportingDocumentRuleDefinition, index: number) => {
      const isNotRequired = !!(rule.condition_json?.is_not_required);
      // For excluded roles, downgrade to normal required rule (condition must match).
      const showAlwaysDefault = !!rule.show_always_default && this.matchesShowAlwaysRoleCondition(rule);
      const showAdditionalCategory = this.isAdditionalCategoryRule(rule);
      const hasCondition = this.hasRuleCondition(rule.condition_json);
      const conditionMatched = !hasCondition || this.evaluateConditionJson(rule.condition_json);
      const optionalRule = (showAlwaysDefault && (!hasCondition || !conditionMatched)) || isNotRequired;

      // For show_always_default rules:
      //   - No condition defined   -> never create a placeholder (empty grid + upload only).
      //   - Condition defined+met  -> create a required placeholder.
      //   - Condition defined+miss -> never create a placeholder (empty grid + upload only).
      // For is_not_required rules: follow normal flow but mark as optional.
      // For mandatory rules (show_always_default = false): keep the original behavior
      // (no-condition implicitly matches; condition must match to render placeholder).
      if (showAlwaysDefault) {
        if (!hasCondition || !conditionMatched) {
          return;
        }
      } else if (!conditionMatched) {
        return;
      }

       const rawCategory = String(rule.cd_document_category || '').trim();
      const isAllCategoryRule = rawCategory.toUpperCase() === 'ALL';
      const sectionName = this.resolveSectionName();
      const placeholderKey = `db-${rule.rule_id || index}-${rawCategory}-${String(rule.cd_document_type || '').trim()}-${sectionName || ''}`;
      // For ALL-category rules, restore the user's previously picked category
      // (if any) so emit/rebuild cycles don't reset the in-grid dropdown.
      const selectedCategoryValue = isAllCategoryRule
        ? (this.placeholderCategorySelections[placeholderKey] || '')
        : '';
      const category = isAllCategoryRule ? selectedCategoryValue : rawCategory;

      // ── Field-based dynamic type resolution ──
      // When a rule specifies dynamic_type_source_field, the document type is
      // read from the form field's current value instead of the static rule
      // cd_document_type. If the field is a dropdown we also store the lookup
      // category so getTypeLabel() can resolve key → display value.
      const dynamicTypeSourceField = String(rule.dynamic_type_source_field || '').trim();
      const dynamicTypeLookupCategory = String(rule.dynamic_type_lookup_category || '').trim();
      let fieldDerivedTypeValue = '';
      if (dynamicTypeSourceField) {
        fieldDerivedTypeValue = String(this.readFieldValue(dynamicTypeSourceField) || '').trim();
      }

      const configuredTypeValue = dynamicTypeSourceField
        ? fieldDerivedTypeValue
        : (isAllCategoryRule ? '' : String(rule.cd_document_type || '').trim());
      const selectedTypeValue = this.placeholderTypeSelections[placeholderKey] || '';
      const typeValue = configuredTypeValue || selectedTypeValue;

      // For ALL-category rules, match an uploaded row by rule_id (the uploaded
      // row carries the rule_id from buildDocumentPayload) instead of by
      // category, because the rule itself doesn't have a fixed category.
      // Fallback: match by _placeholderKey, then by type+section for rows where
      // rule_id was not persisted (e.g. older records or rule_id is falsy).
      const matchedRow = isAllCategoryRule
        ? existingRows.find((row: SupportingDocumentRow) =>
            this.hasUploadedFile(row)
            && !!rule.rule_id
            && String(row.rule_id || '') === String(rule.rule_id)
            && this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '')
          )
          || existingRows.find((row: SupportingDocumentRow) =>
            this.hasUploadedFile(row)
            && !!row._placeholderKey
            && row._placeholderKey === placeholderKey
          )
          || (this.normalizeValue(typeValue) ? existingRows.find((row: SupportingDocumentRow) =>
            this.hasUploadedFile(row)
            && this.normalizeValue(row.cd_document_type || '') === this.normalizeValue(typeValue)
            && this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '')
          ) : undefined)
        : this.findMatchingDocumentRequirement(existingRows, category, typeValue, sectionName);

      if (matchedRow) {
        matchedRow.rule_id = matchedRow.rule_id ?? rule.rule_id ?? null;
        matchedRow._placeholderKey = matchedRow._placeholderKey || placeholderKey;
        matchedRow._lockType = conditionMatched && !!configuredTypeValue;
        matchedRow._optionalRule = matchedRow._optionalRule || optionalRule;
        if (dynamicTypeLookupCategory) {
          matchedRow.dynamic_type_lookup_request_type = dynamicTypeLookupCategory;
          matchedRow._dynamicTypeLookupRequestType = dynamicTypeLookupCategory;
        }
        // Case Document Mode: keep this rule's category/type available in the
        // upload modal even after a document has been uploaded for it, so the
        // matched categories remain consistently selectable.
        if (this.isCaseDocMode()) {
          if (isAllCategoryRule || !String(rawCategory || '').trim()) {
            this.caseDocHasAllCategoryRequirement = true;
          } else {
            this.caseDocUploadRequirements.push({ category: rawCategory, type: typeValue });
          }
        }
        return;
      }

      // When the source field has no value yet, skip placeholder creation for
      // field-based dynamic rules so we don't render an empty-type row.
      if (dynamicTypeSourceField && !fieldDerivedTypeValue) {
        return;
      }

      // Case Document Mode: don't render a placeholder row for this matched
      // rule. Instead record its category/type so it becomes a selectable
      // option in the (additional) upload modal. The grid remains empty until
      // the user uploads a document against one of these categories.
      if (this.isCaseDocMode()) {
        if (isAllCategoryRule || !String(rawCategory || '').trim()) {
          this.caseDocHasAllCategoryRequirement = true;
        } else {
          this.caseDocUploadRequirements.push({ category: rawCategory, type: typeValue });
        }
        return;
      }

      placeholders.push({
        cd_document_category: category,
        cd_document_type: typeValue,
        section_name: sectionName,
        status: '',
        file_name: '',
        file_hash: '',
        isPlaceholder: true,
        _lockType: conditionMatched && !!configuredTypeValue,
        _placeholderKey: placeholderKey,
        _optionalRule: optionalRule,
        _categoryIsAll: isAllCategoryRule,
        _showAdditionalCategory: showAdditionalCategory,
        rule_id: rule.rule_id,
        dynamic_type_lookup_request_type: dynamicTypeLookupCategory || '',
        _dynamicTypeLookupRequestType: dynamicTypeLookupCategory || ''
      });
    });

    return placeholders;
  }

  private buildSourceGridPlaceholderRowsFromDbRules(
    rows: SupportingDocumentRow[],
    activeRules: SupportingDocumentRuleDefinition[]
  ): SupportingDocumentRow[] {
    if (!this.hasDocumentSourceGrid()) {
      return [];
    }

    const placeholders: SupportingDocumentRow[] = [];
    const existingRows = rows.filter((row: SupportingDocumentRow) => !row.isPlaceholder || this.hasUploadedFile(row));
    const createdPlaceholderKeys = new Set<string>();
    const sourceGridKey = String(this.config.dynamicDocuments.sourceGridKey || '').trim();
    const sourceTypeField = String(this.config.dynamicDocuments.sourceTypeField || '').trim();
    const sourceTypeLookupRequestType = String(this.config.dynamicDocuments.sourceTypeLookupRequestType || '').trim();
    const sectionName = this.resolveSectionName();

    this.buildDynamicSourceRecordEntries().forEach(({ record, index, sourceKey }: { record: any; index: number; sourceKey: string }) => {
      activeRules.forEach((rule: SupportingDocumentRuleDefinition, ruleIndex: number) => {
        const isNotRequired = !!(rule.condition_json?.is_not_required);
        // For excluded roles, downgrade to normal required rule (condition must match).
        const showAlwaysDefault = !!rule.show_always_default && this.matchesShowAlwaysRoleCondition(rule);
        const showAdditionalCategory = this.isAdditionalCategoryRule(rule);
        const hasCondition = this.hasRuleCondition(rule.condition_json);
        const conditionMatched = !hasCondition || this.evaluateConditionJson(rule.condition_json);
        const optionalRule = (showAlwaysDefault && (!hasCondition || !conditionMatched)) || isNotRequired;

        // Optional rules only render placeholders when explicit conditions are defined
        // AND match. Without conditions, or when conditions miss, the grid still shows
        // (via hasAlwaysShowActiveRule) but no placeholder row is added.
        if (showAlwaysDefault) {
          if (!hasCondition || !conditionMatched) {
            return;
          }
        } else if (!conditionMatched) {
          return;
        }

        const rawCategory = String(rule.cd_document_category || '').trim();
        const isAllCategoryRule = rawCategory.toUpperCase() === 'ALL';
        const sourceTypeValue = sourceTypeField ? this.readFieldValueFromSource(sourceTypeField, record) : '';

        // ── Field-based dynamic type in dynamic grid mode ──
        const dynamicTypeSourceField = String(rule.dynamic_type_source_field || '').trim();
        const dynamicTypeLookupCategory = String(rule.dynamic_type_lookup_category || '').trim();
        let fieldDerivedTypeValue = '';
        if (dynamicTypeSourceField) {
          fieldDerivedTypeValue = String(this.readFieldValue(dynamicTypeSourceField) || '').trim();
        }

        const configuredTypeValue = dynamicTypeSourceField
          ? fieldDerivedTypeValue
          : String(sourceTypeValue || (isAllCategoryRule ? '' : rule.cd_document_type) || '').trim();
        if (!rawCategory) {
          return;
        }

        const ruleKey = String(rule.rule_id ?? ruleIndex);
        const placeholderKey = `dynamic-${sourceGridKey}-${sourceKey}-${ruleKey}-${rawCategory}-${sectionName || ''}`;
        const legacyPlaceholderKey = `dynamic-${sourceGridKey}-${sourceKey}-${ruleKey}-${rawCategory}-${configuredTypeValue}-${sectionName || ''}`;
        const selectedTypeValue = this.placeholderTypeSelections[placeholderKey]
          || this.placeholderTypeSelections[legacyPlaceholderKey]
          || '';
        const typeValue = configuredTypeValue || selectedTypeValue;
        // For ALL-category rules, hydrate the in-grid category from any saved
        // user pick so emit-driven rebuilds don't reset the dropdown.
        const restoredCategory = isAllCategoryRule
          ? (this.placeholderCategorySelections[placeholderKey] || '')
          : rawCategory;
        const category = restoredCategory;
        if (createdPlaceholderKeys.has(placeholderKey)) {
          return;
        }

        // ── DEBUG: ALL-category matching in dynamic grid ──
        if (isAllCategoryRule) {
          console.log('[DOC-GRID-DEBUG] ALL-category rule match attempt:', {
            sourceKey,
            ruleKey,
            rawCategory,
            typeValue,
            sectionName,
            placeholderKey,
            configuredTypeValue,
            sourceTypeValue,
            existingRowsCount: existingRows.length,
            existingRowsSummary: existingRows.map((r: SupportingDocumentRow) => ({
              doc_id: r.document_id,
              category: r.cd_document_category,
              type: r.cd_document_type,
              section: r.section_name,
              rule_id: r.rule_id,
              dynamicSourceKey: this.getRowDynamicSourceKey(r),
              dynamicRuleId: this.getRowDynamicRuleId(r),
              hasFile: this.hasUploadedFile(r),
              _placeholderKey: r._placeholderKey
            }))
          });
        }

        // For ALL-category rules the category on the uploaded row is whatever
        // the user picked. Match by: 1) sourceKey+ruleKey, 2) rule_id, 3) type+section fallback.
        const matchedRow = isAllCategoryRule
          ? existingRows.find((row: SupportingDocumentRow) =>
              this.hasUploadedFile(row)
              && this.getRowDynamicSourceKey(row) === sourceKey
              && String(this.getRowDynamicRuleId(row) || row.rule_id || '') === ruleKey
              && this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '')
              && (!this.normalizeValue(typeValue)
                || this.normalizeValue(row.cd_document_type || '') === this.normalizeValue(typeValue))
            ) || existingRows.find((row: SupportingDocumentRow) =>
              this.hasUploadedFile(row)
              && !this.getRowDynamicSourceKey(row)
              && !!rule.rule_id
              && String(row.rule_id || '') === String(rule.rule_id)
              && this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '')
            ) || (this.normalizeValue(typeValue) ? existingRows.find((row: SupportingDocumentRow) =>
              this.hasUploadedFile(row)
              && !this.getRowDynamicSourceKey(row)
              && this.normalizeValue(row.cd_document_type || '') === this.normalizeValue(typeValue)
              && this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '')
            ) : undefined)
          : existingRows.find((row: SupportingDocumentRow) =>
              this.getRowDynamicSourceKey(row) === sourceKey
              && String(this.getRowDynamicRuleId(row) || row.rule_id || '') === ruleKey
              && this.rowMatchesRequirement(row, category, typeValue, sectionName)
            ) || existingRows.find((row: SupportingDocumentRow) =>
              !this.getRowDynamicSourceKey(row)
              && this.rowMatchesRequirement(row, category, typeValue, sectionName)
            );

        // ── DEBUG: Match result ──
        if (isAllCategoryRule) {
          console.log('[DOC-GRID-DEBUG] ALL-category match result:', {
            matched: !!matchedRow,
            matchedDocId: matchedRow?.document_id,
            matchedType: matchedRow?.cd_document_type,
            matchedCategory: matchedRow?.cd_document_category,
            willCreatePlaceholder: !matchedRow
          });
        }

        if (matchedRow) {
          matchedRow.rule_id = matchedRow.rule_id ?? rule.rule_id ?? null;
          matchedRow._placeholderKey = matchedRow._placeholderKey || placeholderKey;
          matchedRow._lockType = !!configuredTypeValue;
          matchedRow._optionalRule = matchedRow._optionalRule || optionalRule;
          matchedRow._dynamicSourceKey = matchedRow._dynamicSourceKey || sourceKey;
          matchedRow._dynamicRuleId = matchedRow._dynamicRuleId || ruleKey;
          matchedRow.dynamic_source_key = matchedRow.dynamic_source_key || sourceKey;
          matchedRow.dynamic_rule_id = matchedRow.dynamic_rule_id || ruleKey;
          matchedRow.dynamic_type_lookup_request_type = matchedRow.dynamic_type_lookup_request_type || dynamicTypeLookupCategory || sourceTypeLookupRequestType;
          matchedRow._dynamicTypeLookupRequestType = matchedRow._dynamicTypeLookupRequestType || dynamicTypeLookupCategory || sourceTypeLookupRequestType;
          return;
        }

        // When the source field has no value yet, skip placeholder creation for
        // field-based dynamic rules so we don't render an empty-type row.
        if (dynamicTypeSourceField && !fieldDerivedTypeValue) {
          return;
        }

        createdPlaceholderKeys.add(placeholderKey);
        placeholders.push({
          cd_document_category: category,
          cd_document_type: typeValue,
          section_name: sectionName,
          status: '',
          file_name: '',
          file_hash: '',
          isPlaceholder: true,
          _lockType: !!configuredTypeValue,
          _placeholderKey: placeholderKey,
          _optionalRule: optionalRule,
          _categoryIsAll: isAllCategoryRule,
          _showAdditionalCategory: showAdditionalCategory,
          dynamic_source_key: sourceKey,
          dynamic_rule_id: ruleKey,
          dynamic_type_lookup_request_type: dynamicTypeLookupCategory || sourceTypeLookupRequestType,
          _dynamicSourceKey: sourceKey,
          _dynamicRuleId: ruleKey,
          _dynamicTypeLookupRequestType: dynamicTypeLookupCategory || sourceTypeLookupRequestType,
          rule_id: rule.rule_id
        });
      });
    });

    return placeholders;
  }

  private buildPlaceholderRowsFromConfig(rows: SupportingDocumentRow[]): SupportingDocumentRow[] {
    const placeholders: SupportingDocumentRow[] = [];
    const existingRows = rows.filter((row: SupportingDocumentRow) => !row.isPlaceholder);

    (this.config.requirements || []).forEach((requirement: SupportingDocumentGridRequirement, index: number) => {
      const shouldShow = this.evaluateBoolean(requirement.showWhen || requirement.requiredWhen || 'true', true);
      if (!shouldShow) {
        return;
      }

      const types = this.resolveRequirementTypes(requirement);
      const requiredCount = Math.max(1, Number(requirement.placeholderCount || 1));
      const sectionName = requirement.sectionNameExpression
        ? String(this.evaluateValue(requirement.sectionNameExpression, '') || '')
        : this.resolveSectionName();
      const typePool = types.length ? types : [''];

      for (let placeholderIndex = 0; placeholderIndex < requiredCount; placeholderIndex += 1) {
        const baseTypeValue = typePool[Math.min(placeholderIndex, typePool.length - 1)] || requirement.defaultType || '';
        const placeholderKey = `${index}-${placeholderIndex}-${requirement.category}-${baseTypeValue}-${sectionName || ''}`;
        const selectedTypeValue = this.placeholderTypeSelections[placeholderKey] || '';
        const typeValue = selectedTypeValue || baseTypeValue;
        const alreadyExists = existingRows.some((row: SupportingDocumentRow) =>
          this.hasUploadedFile(row)
          && this.normalizeValue(row.cd_document_category) === this.normalizeValue(requirement.category)
          && this.normalizeValue(row.cd_document_type) === this.normalizeValue(typeValue)
          && this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '')
        );

        if (alreadyExists) {
          continue;
        }

        placeholders.push({
          cd_document_category: requirement.category,
          cd_document_type: typeValue,
          section_name: sectionName,
          status: '',
          file_name: '',
          file_hash: '',
          isPlaceholder: true,
          _lockType: !!requirement.lockType,
          _placeholderKey: placeholderKey
        });
      }
    });

    return placeholders;
  }

  private resolveRequirementTypes(requirement: SupportingDocumentGridRequirement): string[] {
    const fromExpression = requirement.typesExpression
      ? this.evaluateValue<any[]>(requirement.typesExpression, [])
      : [];
    if (Array.isArray(fromExpression) && fromExpression.length) {
      return fromExpression.map((item: any) => String(item));
    }
    if (Array.isArray(requirement.types) && requirement.types.length) {
      return requirement.types.map((item: any) => String(item));
    }
    return [];
  }

  private getDocumentTypes(category: string): SupportingDocumentGridOption[] {
    if (!category) {
      return [];
    }
    const categoryOption = this.resolveCategoryOption(category);
    const nestedTypeOptions = Array.isArray(categoryOption?.lookup) ? categoryOption.lookup : [];
    if (nestedTypeOptions.length) {
      return this.sortDocumentTypeOptions(nestedTypeOptions);
    }
    const parentTypeOptions = String(categoryOption?.lookupId || '').trim()
      ? this.lookupTypesByParentId[String(categoryOption?.lookupId || '').trim()] || []
      : [];
    if (parentTypeOptions.length) {
      return this.sortDocumentTypeOptions(parentTypeOptions);
    }
    const lookupKeys = [
      category,
      categoryOption?.key,
      categoryOption?.value
    ].filter((item: any, index: number, arr: any[]) => !!item && arr.indexOf(item) === index);

    // for (const lookupKey of lookupKeys) {
    //   const direct = this.config.documentTypes[lookupKey];
    //   if (Array.isArray(direct) && direct.length) {
    //     return direct;
    //   }
    // }

    // return Object.entries(this.config.documentTypes || {}).find(([key]: [string, SupportingDocumentGridOption[]]) =>
    //   lookupKeys.some((lookupKey: string) => this.isSameLookupValue(key, lookupKey))
    // )?.[1] || [];
    return [];
  }

  private resolveTypeOption(
    category: string,
    typeValue: string,
    dynamicTypeLookupRequestType = ''
  ): SupportingDocumentGridOption | undefined {
    const normalizedType = this.normalizeValue(typeValue);
    if (!normalizedType) {
      return undefined;
    }

    const categoryTypes = this.getDocumentTypes(category);
    const categoryMatch = categoryTypes.find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, typeValue)
      || this.isSameLookupValue(item.value, typeValue)
      || this.isSameLookupValue(item.lookupId, typeValue)
    );
    if (categoryMatch) {
      return categoryMatch;
    }

    const requestType = String(dynamicTypeLookupRequestType || '').trim();
    if (requestType) {
      this.ensureDynamicTypeLookupLoaded(requestType);
      return (this.dynamicTypeLookupOptions[requestType] || []).find((item: SupportingDocumentGridOption) =>
        this.isSameLookupValue(item.key, typeValue)
        || this.isSameLookupValue(item.value, typeValue)
        || this.isSameLookupValue(item.lookupId, typeValue)
      );
    }

    return this.lookupDocumentTypes.find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, typeValue)
      || this.isSameLookupValue(item.value, typeValue)
      || this.isSameLookupValue(item.lookupId, typeValue)
    );
  }

  private sortDocumentTypeOptions(options: SupportingDocumentGridOption[]): SupportingDocumentGridOption[] {
    return [...(options || [])].sort((left: SupportingDocumentGridOption, right: SupportingDocumentGridOption) =>
      String(left?.value || left?.key || '').localeCompare(String(right?.value || right?.key || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
  }

  private resolveCategoryOption(category: any): SupportingDocumentGridOption | undefined {
    return this.getAllCategoryOptions().find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, category)
      || this.isSameLookupValue(item.value, category)
      || this.isSameLookupValue(item.lookupId, category)
    );
  }

  private isSameLookupValue(left: any, right: any): boolean {
    return this.normalizeValue(left) === this.normalizeValue(right);
  }

  private getAvailableCategories(useAdditionalSection?: boolean): SupportingDocumentGridOption[] {
    // Case Document Mode: only expose the categories collected from matched
    // rules (surfaced in the additional upload modal). Fall back to the normal
    // resolution when no specific requirements were collected (e.g. only
    // ALL-category rules, or dynamic-source grid mode).
    if (this.isCaseDocMode()) {
      const caseDocCategories = this.getCaseDocRequirementCategories();
      if (caseDocCategories.length && !this.caseDocHasAllCategoryRequirement) {
        return caseDocCategories;
      }
    }
    const shouldUseAdditionalSection = useAdditionalSection ?? this.shouldUseAdditionalCategoriesForUpload();
    const endpointCategories = (this.lookupCategories || []).filter((item: SupportingDocumentGridOption) => this.matchesCategorySection(item, shouldUseAdditionalSection));
    const source = endpointCategories.length ? endpointCategories : [...(this.config.categories || []), ...(this.lookupCategories || [])];
    return source
      .filter((item: SupportingDocumentGridOption, index: number, source: SupportingDocumentGridOption[]) =>
        !!item?.key && !!item?.value
        && source.findIndex((candidate: SupportingDocumentGridOption) =>
          this.isSameLookupValue(candidate.key, item.key) || this.isSameLookupValue(candidate.value, item.value)
        ) === index
      )
      .filter((item: SupportingDocumentGridOption) => !this.isCategoryHiddenForCurrentRole(item));
  }

  /**
   * Case Document Mode helper. Resolves the distinct categories collected from
   * matched rules (which are no longer rendered as placeholder rows) into
   * category options for the upload modal dropdown. Categories hidden for the
   * current role are excluded.
   */
  private getCaseDocRequirementCategories(): SupportingDocumentGridOption[] {
    const seen = new Set<string>();
    const result: SupportingDocumentGridOption[] = [];
    for (const requirement of this.caseDocUploadRequirements) {
      const normalizedKey = this.normalizeValue(requirement.category);
      if (!normalizedKey || seen.has(normalizedKey)) {
        continue;
      }
      seen.add(normalizedKey);
      const option = this.resolveCategoryOption(requirement.category);
      const resolved = option?.key && option?.value
        ? option
        : ({ key: requirement.category, value: requirement.category } as SupportingDocumentGridOption);
      if (!this.isCategoryHiddenForCurrentRole(resolved)) {
        result.push(resolved);
      }
    }
    return result;
  }

  /**
   * Case Document Mode helper. Returns the upload modal Type options for the
   * given category filtered to only the types collected from matched rules. If
   * the matched rules for the category carry no specific type, all types for
   * the category are returned.
   */
  private getCaseDocTypeOptionsForCategory(category: string): SupportingDocumentGridOption[] {
    const allTypes = this.getDocumentTypes(category);
    const requiredTypeKeys = new Set<string>(
      this.caseDocUploadRequirements
        .filter((requirement: { category: string; type: string }) =>
          this.isSameLookupValue(requirement.category, category) && !!String(requirement.type || '').trim()
        )
        .map((requirement: { category: string; type: string }) => this.normalizeValue(requirement.type))
    );
    if (!requiredTypeKeys.size) {
      return this.sortDocumentTypeOptions(allTypes);
    }
    const filtered = allTypes.filter((item: SupportingDocumentGridOption) =>
      requiredTypeKeys.has(this.normalizeValue(item.key))
      || requiredTypeKeys.has(this.normalizeValue(item.value))
      || requiredTypeKeys.has(this.normalizeValue(item.lookupId))
    );
    return this.sortDocumentTypeOptions(filtered.length ? filtered : allTypes);
  }

  private isCategoryHiddenForCurrentRole(item: SupportingDocumentGridOption): boolean {
    const roleId = String(this.sessionStorage.getItem('role_id') || '').trim();
    if (!roleId) {
      return false;
    }
    const lookupItem = this.findLookupCategoryByKey(item?.key);
    let optionalData = lookupItem?.optionaldata ?? item?.optionaldata;
    if (typeof optionalData === 'string') {
      try {
        optionalData = JSON.parse(optionalData);
      } catch {
        optionalData = null;
      }
    }
    if (!optionalData || typeof optionalData !== 'object') {
      return false;
    }
    const hiddenRoles: any[] = optionalData?.role || [];
    if (!Array.isArray(hiddenRoles) || !hiddenRoles.length) {
      return false;
    }
    return hiddenRoles.some((r: any) => String(r).trim() === roleId);
  }

  private findLookupCategoryByKey(key: string): SupportingDocumentGridOption | undefined {
    if (!key) {
      return undefined;
    }
    return (this.lookupCategories || []).find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, key)
    );
  }

  private getAllCategoryOptions(): SupportingDocumentGridOption[] {

    return [...(this.config.categories || []), ...(this.lookupCategories || [])]
      .filter((item: SupportingDocumentGridOption, index: number, source: SupportingDocumentGridOption[]) =>
        !!item?.key && !!item?.value
        && source.findIndex((candidate: SupportingDocumentGridOption) =>
          this.isSameLookupValue(candidate.key, item.key)
        ) === index
      );
  }

  private loadLookupData(): void {
    this.loadLookupMessages();
    this.supportingDocumentRuleConfigService.getSupportingDocumentCategoriesWithTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows: SupportingDocumentGridOption[]) => {
          this.lookupCategories = (rows || []).map((item: SupportingDocumentGridOption) => ({
            ...item,
            lookup: this.sortDocumentTypeOptions(item.lookup || [])
          }));
          this.lookupDocumentTypes = this.sortDocumentTypeOptions(this.lookupCategories.flatMap((item: SupportingDocumentGridOption) => item.lookup || []));
          this.lookupTypesByParentId = this.lookupCategories.reduce((acc: Record<string, SupportingDocumentGridOption[]>, item: SupportingDocumentGridOption) => {
            const lookupId = String(item.lookupId || '').trim();
            const childLookups = Array.isArray(item.lookup) ? item.lookup : [];
            if (lookupId && childLookups.length) {
              acc[lookupId] = this.sortDocumentTypeOptions(childLookups);
            }
            return acc;
          }, {});
          // Re-sync additional dynamic types now that lookup categories are
          // available for proper category resolution.
          this.syncAdditionalDynamicTypeRecords();
          this.rememberSyncedTargetRowsLoadedForSection(this.getCurrentSectionName(), this.rawDocuments);
          this.rebuildDerivedState();
          this.syncUploadCategorySelection(true);
          this.syncUploadTypes(this.resolveUploadCategory(), true);
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.warn('Unable to load supporting document category lookup data.', error);
          this.supportingDocumentRuleConfigService.getDocumentLookupData()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: ({ categories, documentTypes }) => {
                this.lookupCategories = (categories || []).map((item: any) => ({
                  key: String(item?.value || item?.key || '').trim(),
                  value: String(item?.label || item?.value || '').trim(),
                  lookupId: String(item?.value || item?.key || '').trim()
                })).filter((item: SupportingDocumentGridOption) => !!item.key && !!item.value);

                this.lookupDocumentTypes = this.sortDocumentTypeOptions((documentTypes || []).map((item: any) => ({
                  key: String(item?.value || item?.key || '').trim(),
                  value: String(item?.label || item?.value || '').trim()
                })).filter((item: SupportingDocumentGridOption) => !!item.key && !!item.value));
                this.lookupTypesByParentId = {};
                this.syncUploadCategorySelection(true);
                this.syncUploadTypes(this.resolveUploadCategory(), true);
                this.cdr.detectChanges();
              },
              error: (lookupError: any) => {
                console.warn('Unable to load supporting document lookup fallback data.', lookupError);
              }
            });
        }
      });
  }

  private loadDocumentStatuses(): void {
    this.supportingDocumentRuleConfigService.getSupportingDocumentStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (statuses: SupportingDocumentGridStatusConfig) => {
          this.resolvedDocumentStatuses = statuses;
          this.applyResolvedDocumentStatuses();
          this.rebuildDerivedState();
        },
        error: (error: any) => {
          console.warn('Unable to load supporting document status lookup data.', error);
        }
      });
  }

  private applyResolvedDocumentStatuses(): void {
    if (!this.resolvedDocumentStatuses) {
      return;
    }

    this.config = {
      ...this.config,
      statuses: {
        ...this.config.statuses,
        ...this.resolvedDocumentStatuses
      }
    };
  }

  private validateFile(file: File): string {
    const allowedExtensions = this.config.validation.allowedExtensions.map((item: string) => item.toLowerCase());
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (allowedExtensions.length && !allowedExtensions.includes(extension)) {
      return this.errorMessage['M0129']
        || this.config.validation.invalidTypeMessage
        || `Unsupported file type. Allowed: ${allowedExtensions.join(', ')}.`;
    }
    const maxBytes = this.config.validation.maxFileSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      return this.errorMessage['M0128']
        || this.config.validation.maxSizeMessage
        || `File size exceeds ${this.config.validation.maxFileSizeMb} MB.`;
    }
    return '';
  }

  private async uploadFile(file: File): Promise<{ name: string; path: string; attachmenttype: string }> {
    const pageId = this.resolveEffectiveFormId();
    const responseId = this.resolveResponseId();
    const suffix = responseId ? `/${responseId}` : '';
    const payload = {
      fileName: `${pageId || 'supporting-documents'}${suffix}/${file.name}`,
      contentType: file.type,
      type: 'supporting-document'
    };

    const keyResponse: any = await this.pageBuilderService.getUploadKey(payload).toPromise();
    const uploadUrl = keyResponse?.data;
    await this.httpService.putUpload(uploadUrl, file, file.type).toPromise();
    const fileUrl = typeof uploadUrl === 'string' ? uploadUrl.split('?')[0] : uploadUrl;

    const attachmentPayload = {
      responseid: Number(responseId),
      type: 'supporting-document',
      formid: Number(pageId),
      pageId: Number(pageId),
      field: this.name || 'supportingDocuments',
      attachmentdetails: [
        {
          name: file.name,
          path: fileUrl,
          attachmenttype: file.type,
          field: this.name || 'supportingDocuments'
        }
      ]
    };

    if (responseId && pageId) {
      try {
        await this.pageBuilderService.createFormResponseAttachment(attachmentPayload).toPromise();
      } catch (error) {
        console.warn('Attachment metadata save failed; keeping file upload result for document row.', error);
      }
    }

    return {
      name: file.name,
      path: fileUrl,
      attachmenttype: file.type
    };
  }

  private buildDocumentPayload(uploaded: { name: string; path: string; attachmenttype: string }): SupportingDocumentRow {
    const userId = Number(this.sessionStorage.getItem('id') || 0) || null;
    const ruleId = this.uploadTargetRow?.isPlaceholder
      ? Number(this.uploadTargetRow?.rule_id || 0) || null
      : null;

    return {
      application_id: this.resolveApplicationId(),
      person_id: this.resolvePersonId(),
      formid: this.resolveEffectiveFormId(),
      cd_document_category: this.uploadDraft.category || this.uploadTargetRow?.cd_document_category || '',
      cd_document_type: this.uploadDraft.docType || this.uploadTargetRow?.cd_document_type || '',
      file_name: uploaded.name,
      file_hash: uploaded.path,
      path: uploaded.path,
      section_name: String(this.resolveSectionName(this.uploadTargetRow) || '').trim(),
      status: this.isCaseDocMode() ? (this.config.statuses.case || 'Case') : this.config.statuses.pendingReview,
      document_notes: '',
      created_by: userId,
      updated_by: userId,
      created_by_name: this.resolveUserDisplayName(),
      uploaded_at: new Date().toISOString(),
      is_active: true,
      rule_id: ruleId,
      old_id: this.uploadMode === 'replace' ? String(this.uploadTargetRow?.document_id || this.uploadTargetRow?.old_id || '') : null,
      _placeholderKey: this.uploadTargetRow?._placeholderKey,
      dynamic_source_key: this.getRowDynamicSourceKey(this.uploadTargetRow),
      dynamic_rule_id: this.getRowDynamicRuleId(this.uploadTargetRow),
      dynamic_type_lookup_request_type: this.resolveUploadDynamicTypeLookupRequestType(),
      _dynamicSourceKey: this.uploadTargetRow?._dynamicSourceKey,
      _dynamicRuleId: this.uploadTargetRow?._dynamicRuleId,
      _dynamicTypeLookupRequestType: this.resolveUploadDynamicTypeLookupRequestType()
    };
  }

  private async persistDocument(payload: SupportingDocumentRow, row: SupportingDocumentRow | null): Promise<SupportingDocumentRow> {
    if (this.uploadMode === 'replace' && row) {
      await this.persistStatus(row, this.config.statuses.archived, row.document_notes || '');
    }

    if (this.uploadMode === 'replace' && row && this.config.api.replaceEndpoint) {
      const response: any = await this.httpService.post(this.resolveEndpoint(this.config.api.replaceEndpoint, row), payload).toPromise();
      const data = this.extractSavedDocument(response);
      const persisted = {
        ...payload,
        ...data,
        document_id: data?.document_id
          ?? data?.documentId
          ?? data?.new_document_id
          ?? data?.newDocumentId
          ?? data?.id
          ?? data?.primary_id
          ?? data?.primaryId
          ?? null,
        dynamic_type_lookup_request_type: data?.dynamic_type_lookup_request_type || data?.dynamicTypeLookupRequestType || payload.dynamic_type_lookup_request_type || '',
        _dynamicTypeLookupRequestType: data?._dynamicTypeLookupRequestType || data?.dynamic_type_lookup_request_type || data?.dynamicTypeLookupRequestType || payload._dynamicTypeLookupRequestType || ''
      };
      await this.persistSyncDocumentsOnUpload(persisted, payload);
      return persisted;
    }

    if (this.config.api.saveEndpoint) {
      const response: any = await this.httpService.post(this.resolveEndpoint(this.config.api.saveEndpoint, row), payload).toPromise();
      const data = response?.data || response;
      const persisted = {
        ...payload,
        ...data,
        document_id: data?.document_id ?? data?.id ?? payload.document_id ?? null,
        dynamic_type_lookup_request_type: data?.dynamic_type_lookup_request_type || data?.dynamicTypeLookupRequestType || payload.dynamic_type_lookup_request_type || '',
        _dynamicTypeLookupRequestType: data?._dynamicTypeLookupRequestType || data?.dynamic_type_lookup_request_type || data?.dynamicTypeLookupRequestType || payload._dynamicTypeLookupRequestType || ''
      };
      await this.persistSyncDocumentsOnUpload(persisted, payload);
      return persisted;
    }

    const fallback = {
      ...payload,
      document_id: row?.document_id || Date.now()
    };
    await this.persistSyncDocumentsOnUpload(fallback, payload);
    return fallback;
  }

  private async persistStatus(row: SupportingDocumentRow, status: string, notes: string): Promise<any> {
    const payload = {
      document_id: row.document_id,
      application_id: row.application_id,
      status,
      document_notes: notes,
      updated_by: Number(this.sessionStorage.getItem('id') || 0) || null,
      section_name: row.section_name || ''
    };

    let response: any;
    if (this.config.api.updateStatusEndpoint) {
      response = await this.httpService.put(this.resolveEndpoint(this.config.api.updateStatusEndpoint, row), payload).toPromise();
    } else {
      response = payload;
    }

    await this.persistSyncDocumentsOnStatusChange(row, payload);
    return response;
  }

  private async persistDelete(row: SupportingDocumentRow): Promise<any> {
    const payload = {
      document_id: row.document_id,
      application_id: row.application_id,
      status: this.config.statuses.deleted,
      updated_by: Number(this.sessionStorage.getItem('id') || 0) || null,
      section_name: row.section_name || ''
    };

    if (this.config.api.deleteEndpoint) {
      const response = await this.httpService.delete(this.resolveEndpoint(this.config.api.deleteEndpoint, row)).toPromise();
      await this.persistSyncDocumentsOnDelete(row, payload);
      return response;
    }

    await this.persistSyncDocumentsOnDelete(row, payload);
    return payload;
  }

  private extractSavedDocument(response: any): any {
    const data = response?.data?.data ?? response?.data ?? response;
    if (Array.isArray(data)) {
      return data[0] || {};
    }
    return data || {};
  }

  private async hydrateReplacementDocumentId(row: SupportingDocumentRow): Promise<SupportingDocumentRow> {
    if (row?.document_id || this.uploadMode !== 'replace') {
      return row;
    }

    try {
      const request = this.buildDocumentListRequest();
      if (!request.requiredParamsPresent) {
        return row;
      }
      const response = await request.request$.toPromise();
      const documents = this.normalizeIncomingDocuments(this.extractDocumentRows(response));
      const matched = documents.find((candidate: SupportingDocumentRow) =>
        !!candidate.document_id
        && !this.isArchived(candidate)
        && this.normalizeStatus(candidate.status) !== this.normalizeStatus(this.config.statuses.deleted)
        && this.isReplacementDocumentMatch(candidate, row)
      );

      return matched ? { ...row, document_id: matched.document_id } : row;
    } catch (error) {
      console.warn('Unable to hydrate replacement document id.', error);
      return row;
    }
  }

  private isReplacementDocumentMatch(candidate: SupportingDocumentRow, row: SupportingDocumentRow): boolean {
    const pathMatches = !!this.normalizeValue(row.path || row.file_hash)
      && (
        this.normalizeValue(candidate.path || '') === this.normalizeValue(row.path || row.file_hash)
        || this.normalizeValue(candidate.file_hash || '') === this.normalizeValue(row.path || row.file_hash)
      );
    const fileMatches = this.normalizeValue(candidate.file_name || '') === this.normalizeValue(row.file_name || '');
    const categoryMatches = this.normalizeValue(candidate.cd_document_category || '') === this.normalizeValue(row.cd_document_category || '');
    const typeMatches = this.normalizeValue(candidate.cd_document_type || '') === this.normalizeValue(row.cd_document_type || '');
    const sectionMatches = this.normalizeValue(candidate.section_name || '') === this.normalizeValue(row.section_name || '');
    const oldIdMatches = !!String(row.old_id || '').trim() && String((candidate as any).old_id || '').trim() === String(row.old_id || '').trim();

    return sectionMatches && categoryMatches && typeMatches && (pathMatches || (fileMatches && oldIdMatches));
  }

  private async persistSyncDocumentsOnUpload(sourceDocument: SupportingDocumentRow, sourcePayload: SupportingDocumentRow): Promise<void> {
    if (!this.config.api.syncSaveEndpoint || this.isSystemManagedDocument(sourceDocument)) {
      return;
    }

    const syncDocuments = this.getSyncDocumentDefinitionsForRow(sourcePayload);
    if (!syncDocuments.length) {
      return;
    }

    const payload = {
      action: this.uploadMode === 'replace' ? 'replace' : 'upload',
      sourceDocument,
      sourcePayload,
      syncDocuments,
      old_document_id: this.uploadMode === 'replace'
        ? String(sourceDocument.old_id || sourcePayload.old_id || '')
        : null,
      archive_status: this.uploadMode === 'replace' ? this.config.statuses.archived : null,
      application_id: sourceDocument.application_id || this.resolveApplicationId(),
      person_id: sourceDocument.person_id || this.resolvePersonId(),
      formid: sourceDocument.formid || this.resolveEffectiveFormId(),
      section_name: sourceDocument.section_name || this.resolveSectionName(sourceDocument),
      updated_by: Number(this.sessionStorage.getItem('id') || 0) || null
    };

    await this.httpService.post(this.resolveEndpoint(this.config.api.syncSaveEndpoint, sourceDocument), payload).toPromise();
    this.notifySyncedSectionsChanged(syncDocuments, sourceDocument);
  }

  private async persistSyncDocumentsOnDelete(sourceDocument: SupportingDocumentRow, deletePayload: any): Promise<void> {
    if (!this.config.api.syncDeleteEndpoint || this.isSystemManagedDocument(sourceDocument)) {
      return;
    }

    const payload = {
      action: 'delete',
      sourceDocument,
      deletePayload,
      syncDocuments: this.getSyncDocumentDefinitionsForRow(sourceDocument),
      document_id: sourceDocument.document_id,
      application_id: sourceDocument.application_id || this.resolveApplicationId(),
      section_name: sourceDocument.section_name || this.resolveSectionName(sourceDocument),
      updated_by: Number(this.sessionStorage.getItem('id') || 0) || null
    };

    await this.httpService.post(this.resolveEndpoint(this.config.api.syncDeleteEndpoint, sourceDocument), payload).toPromise();
    this.notifySyncedSectionsChanged(payload.syncDocuments, sourceDocument);
  }

  private async persistSyncDocumentsOnStatusChange(sourceDocument: SupportingDocumentRow, statusPayload: any): Promise<void> {
    if (!this.config.api.syncSaveEndpoint || this.isSystemManagedDocument(sourceDocument)) {
      return;
    }

    const syncDocuments = this.getSyncDocumentDefinitionsForRow(sourceDocument);
    if (!syncDocuments.length) {
      return;
    }

    const syncedSourceDocument = {
      ...sourceDocument,
      status: statusPayload?.status || sourceDocument.status,
      document_notes: statusPayload?.document_notes ?? sourceDocument.document_notes
    };

    const payload = {
      action: 'status',
      sourceDocument: syncedSourceDocument,
      statusPayload,
      syncDocuments,
      document_id: sourceDocument.document_id,
      status: statusPayload?.status || '',
      document_notes: statusPayload?.document_notes || '',
      application_id: sourceDocument.application_id || this.resolveApplicationId(),
      person_id: sourceDocument.person_id || this.resolvePersonId(),
      formid: sourceDocument.formid || this.resolveEffectiveFormId(),
      section_name: sourceDocument.section_name || this.resolveSectionName(sourceDocument),
      updated_by: Number(this.sessionStorage.getItem('id') || 0) || null
    };

    await this.httpService.post(this.resolveEndpoint(this.config.api.syncSaveEndpoint, sourceDocument), payload).toPromise();
    this.notifySyncedSectionsChanged(syncDocuments, sourceDocument);
  }

  private getSyncDocumentDefinitionsForRow(row: SupportingDocumentRow): SupportingDocumentSyncDefinition[] {
    const typeOption = this.resolveTypeOption(
      row.cd_document_category || '',
      row.cd_document_type || '',
      this.getRowDynamicTypeLookupRequestType(row)
    );
    return this.getSyncDocumentsFromOptionalData(typeOption?.optionaldata);
  }

  private notifySyncedSectionsChanged(syncDocuments: SupportingDocumentSyncDefinition[], sourceDocument?: SupportingDocumentRow): void {
    const targetSections = (syncDocuments || [])
      .map((item: SupportingDocumentSyncDefinition) => item.targetSection || item.targetSectionName)
      .filter((item: any, index: number, source: any[]) =>
        !!String(item || '').trim()
        && source.findIndex((candidate: any) => this.normalizeValue(candidate) === this.normalizeValue(item)) === index
      );

    window.dispatchEvent(new CustomEvent('supporting-documents-sync-changed', {
      detail: {
        targetSections,
        sourceDocumentId: sourceDocument?.document_id || ''
      }
    }));
  }

  private hasDuplicate(payload: SupportingDocumentRow): boolean {
    const matchOn = this.config.duplicate.matchOn || [];
    return this.rawDocuments.some((row: SupportingDocumentRow) => {
      if (!row.file_name || this.normalizeStatus(row.status) === this.normalizeStatus(this.config.statuses.deleted)) {
        return false;
      }
      if (this.uploadTargetRow && this.isSameRow(row, this.uploadTargetRow)) {
        return false;
      }
      return matchOn.every((field: string) => {
        switch (field) {
          case 'fileName':
            return this.normalizeValue(row.file_name) === this.normalizeValue(payload.file_name);
          case 'category':
            return this.normalizeValue(row.cd_document_category) === this.normalizeValue(payload.cd_document_category);
          case 'docType':
            return this.normalizeValue(row.cd_document_type) === this.normalizeValue(payload.cd_document_type);
          case 'sectionName':
            return this.normalizeValue(row.section_name) === this.normalizeValue(payload.section_name);
          default:
            return false;
        }
      });
    });
  }

  private upsertRow(nextRow: SupportingDocumentRow, targetRow: SupportingDocumentRow | null): void {
    let matchedTargetRow = false;

    if (targetRow) {
      this.rawDocuments = this.rawDocuments.map((row: SupportingDocumentRow) => {
        if (this.isSameRow(row, targetRow)) {
          matchedTargetRow = true;
          if (this.uploadMode === 'replace') {
            return { ...row, status: this.config.statuses.archived };
          }
          return { ...row, ...nextRow, isPlaceholder: false };
        }
        if (this.uploadMode === 'replace' && this.isSyncedTargetOfSource(targetRow, row)) {
          return { ...row, status: this.config.statuses.archived };
        }
        return row;
      });
    }

    if (!targetRow || this.uploadMode === 'replace' || !matchedTargetRow) {
      this.rawDocuments = [...this.rawDocuments, { ...nextRow, isPlaceholder: false }];
    }
  }

  private hasUploadedFile(row: SupportingDocumentRow | null | undefined): boolean {
    return !!String(row?.file_name || '').trim() && !!String(row?.path || row?.file_hash || '').trim();
  }

  private applyStatusToRows(sourceRow: SupportingDocumentRow, status: string, notes: string, updated?: any): SupportingDocumentRow[] {
    return this.rawDocuments.map((item: SupportingDocumentRow) => {
      if (this.isSameRow(item, sourceRow)) {
        return { ...item, ...updated, status, document_notes: notes || item.document_notes };
      }
      if (this.isSyncedTargetOfSource(sourceRow, item)) {
        return { ...item, status, document_notes: notes || item.document_notes };
      }
      return item;
    });
  }

  private isSyncedTargetOfSource(sourceRow: SupportingDocumentRow | null | undefined, targetRow: SupportingDocumentRow | null | undefined): boolean {
    if (!sourceRow || !targetRow || this.isSameRow(sourceRow, targetRow) || !this.hasUploadedFile(targetRow)) {
      return false;
    }

    const sourceDocumentId = String(sourceRow.document_id || '').trim();
    const mirroredFromDocumentId = String(targetRow.mirrored_from_document_id ?? targetRow.mirroredFromDocumentId ?? '').trim();
    if (sourceDocumentId && mirroredFromDocumentId && sourceDocumentId === mirroredFromDocumentId) {
      return true;
    }

    return this.getSyncDocumentDefinitionsForRow(sourceRow).some((syncDocument: SupportingDocumentSyncDefinition) =>
      this.syncDefinitionTargetsRow(syncDocument, targetRow)
      && this.hasMatchingSyncedFile(sourceRow, targetRow)
    );
  }

  private isSystemManagedDocument(row: SupportingDocumentRow | null | undefined): boolean {
    if (!row) {
      return false;
    }
    return this.hasExplicitSystemManagedMetadata(row) || this.isInferredPrefilledSyncDocument(row);
  }

  private hasExplicitSystemManagedMetadata(row: SupportingDocumentRow | null | undefined): boolean {
    if (!row) {
      return false;
    }
    const additionalInfo = this.parseAdditionalInfo(row.additional_info ?? row.additionalInfo);
    return this.toBoolean(row.is_system_generated)
      || this.toBoolean(row.isSystemGenerated)
      || this.toBoolean(additionalInfo?.is_system_generated)
      || this.toBoolean(additionalInfo?.isSystemGenerated)
      || !!String(row.mirrored_from_document_id ?? row.mirroredFromDocumentId ?? '').trim()
      || !!String(additionalInfo?.mirrored_from_document_id ?? additionalInfo?.mirroredFromDocumentId ?? '').trim();
  }

  private isInferredPrefilledSyncDocument(row: SupportingDocumentRow): boolean {
    if (!this.hasUploadedFile(row)) {
      return false;
    }
    if (this.isRememberedSyncedTargetRow(row)) {
      return true;
    }

    return (this.rawDocuments || []).some((sourceRow: SupportingDocumentRow) => {
      if (!sourceRow || this.isSameRow(sourceRow, row) || !this.hasUploadedFile(sourceRow)) {
        return false;
      }
      if (this.hasExplicitSystemManagedMetadata(sourceRow)) {
        return false;
      }

      return this.getSyncDocumentDefinitionsForRow(sourceRow).some((syncDocument: SupportingDocumentSyncDefinition) =>
        this.syncDefinitionTargetsRow(syncDocument, row)
        && this.hasMatchingSyncedFile(sourceRow, row)
      );
    });
  }

  private getRowSortTime(row: SupportingDocumentRow): number {
    const dateValue = row?.uploaded_at || (row as any)?.created_date || (row as any)?.createdat || '';
    const parsedDate = dateValue ? new Date(String(dateValue)).getTime() : NaN;
    if (!Number.isNaN(parsedDate)) {
      return parsedDate;
    }
    return Number(row?.document_id || Number.MAX_SAFE_INTEGER);
  }

  private shouldCountRowForGridVisibility(row: SupportingDocumentRow): boolean {
    if (!this.isSystemManagedDocument(row)) {
      return true;
    }
    return this.matchesActiveDocumentRule(row);
  }

  private isRequiredUploadedRow(row: SupportingDocumentRow): boolean {
    return this.hasExplicitSystemManagedMetadata(row)
      || (this.isInferredPrefilledSyncDocument(row) && this.matchesActiveDocumentRule(row));
  }

  private matchesActiveDocumentRule(row: SupportingDocumentRow): boolean {
    if (!row || row._optionalRule) {
      return false;
    }
    if (this.dbRules.length) {
      return this.matchesActiveDbRule(row);
    }
    return this.matchesActiveConfiguredRequirement(row);
  }

  private syncDefinitionTargetsRow(syncDocument: SupportingDocumentSyncDefinition, row: SupportingDocumentRow): boolean {
    const rowSection = String(row.section_name || this.resolveSectionName(row) || '').trim();
    const rowCategory = String(row.cd_document_category || '').trim();
    const rowType = String(row.cd_document_type || '').trim();
    if (!rowCategory || !rowType) {
      return false;
    }

    const targetSection = String(syncDocument.targetSection || syncDocument.targetSectionName || '').trim();
    const targetCategory = String(
      syncDocument.targetCategoryKey
      ?? syncDocument.targetCategoryId
      ?? syncDocument.targetCategory
      ?? syncDocument.target_category
      ?? ''
    ).trim();
    const targetType = String(
      syncDocument.targetTypeKey
      ?? syncDocument.targetTypeId
      ?? syncDocument.targetType
      ?? syncDocument.target_type
      ?? ''
    ).trim();

    const sectionMatches = !targetSection || this.normalizeValue(targetSection) === this.normalizeValue(rowSection);
    const categoryMatches = this.normalizeValue(targetCategory) === this.normalizeValue(rowCategory);
    const typeMatches = this.normalizeValue(targetType) === this.normalizeValue(rowType);

    return sectionMatches && categoryMatches && typeMatches;
  }

  private matchesConfiguredSyncTarget(row: SupportingDocumentRow): boolean {
    return this.getAllConfiguredSyncDocuments().some((syncDocument: SupportingDocumentSyncDefinition) =>
      this.syncDefinitionTargetsRow(syncDocument, row)
    );
  }

  private isRememberedSyncedTargetRow(row: SupportingDocumentRow): boolean {
    const sectionKey = this.normalizeValue(row.section_name || this.resolveSectionName(row) || this.getCurrentSectionName());
    const rowKey = this.getSyncedTargetRowKey(row);
    return !!rowKey && (this.syncedTargetRowsBySection[sectionKey] || []).includes(rowKey);
  }

  private getSyncedTargetRowKey(row: SupportingDocumentRow): string {
    const documentId = String(row?.document_id || '').trim();
    if (documentId) {
      return `id:${documentId}`;
    }

    return [
      'file',
      this.normalizeValue(row?.section_name || this.resolveSectionName(row) || ''),
      this.normalizeValue(row?.cd_document_category || ''),
      this.normalizeValue(row?.cd_document_type || ''),
      this.normalizeValue(row?.file_name || ''),
      this.normalizeValue(row?.path || row?.file_hash || ''),
      this.normalizeValue(row?.uploaded_at || '')
    ].join('|');
  }

  private hasMatchingSyncedFile(sourceRow: SupportingDocumentRow, targetRow: SupportingDocumentRow): boolean {
    const sourcePath = this.normalizeValue(sourceRow.path || sourceRow.file_hash || '');
    const targetPath = this.normalizeValue(targetRow.path || targetRow.file_hash || '');
    if (sourcePath && targetPath && sourcePath === targetPath) {
      return true;
    }

    const sourceName = this.normalizeValue(sourceRow.file_name || '');
    const targetName = this.normalizeValue(targetRow.file_name || '');
    const sourceUploadedAt = this.normalizeValue(sourceRow.uploaded_at || '');
    const targetUploadedAt = this.normalizeValue(targetRow.uploaded_at || '');

    return !!sourceName
      && sourceName === targetName
      && !!sourceUploadedAt
      && sourceUploadedAt === targetUploadedAt;
  }

  private getAllConfiguredSyncDocuments(): SupportingDocumentSyncDefinition[] {
    return [
      ...(this.lookupDocumentTypes || []),
      ...(this.lookupCategories || []).flatMap((category: SupportingDocumentGridOption) => category.lookup || [])
    ].flatMap((option: SupportingDocumentGridOption) => this.getSyncDocumentsFromOptionalData(option?.optionaldata));
  }

  private getSyncDocumentsFromOptionalData(optionalData: any): SupportingDocumentSyncDefinition[] {
    if (!optionalData || typeof optionalData !== 'object') {
      return [];
    }

    const syncSource = optionalData.syncDocuments
      || optionalData.syncDocument
      || optionalData.mirrorDocuments
      || optionalData.mirrorDocument
      || optionalData.mirroredDocuments
      || optionalData.mirroredDocument;

    const syncDocuments = Array.isArray(syncSource)
      ? syncSource
      : syncSource
        ? [syncSource]
        : [];

    return syncDocuments.filter((item: any) => item && typeof item === 'object');
  }

  private parseAdditionalInfo(value: any): any {
    if (!value) {
      return {};
    }
    if (typeof value === 'object') {
      return value;
    }
    if (typeof value !== 'string') {
      return {};
    }
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  private toBoolean(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    return ['true', '1', 'yes', 'y', 't'].includes(String(value || '').trim().toLowerCase());
  }

  private openDuplicateModal(template: TemplateRef<any> | undefined): void {
    if (!template) {
      return;
    }
    this.duplicateModalRef = this.modalService.open(template, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'add-appointment-details'
    });
  }

  private evaluateAction(expression: string, row?: SupportingDocumentRow, featureAliases: string[] = []): boolean {
    return this.evaluateFeatureFlag(expression, false, featureAliases, row);
  }

  private evaluateBoolean(expression: string, fallback: boolean): boolean {
    return this.ruleService.evaluate<boolean>(expression, this.buildContext(), fallback);
  }

  private evaluateValue<T>(expression: string, fallback: T): T {
    return this.ruleService.evaluate<T>(expression, this.buildContext(), fallback);
  }

  private buildContext(row?: SupportingDocumentRow): Record<string, any> {
    const currentSectionName = this.getCurrentSectionName();
    const missingRequiredDocuments = this.getMissingRequiredDocuments();
    return {
      data: this.formData?.data || this.formData || {},
      row: row || {},
      user: {
        id: Number(this.sessionStorage.getItem('id') || 0) || null,
        roleId: this.resolveCurrentUserRoleId(),
        username: this.sessionStorage.getItem('username') || ''
      },
      documents: this.rawDocuments,
      rules: this.dbRules,
      application: {
        id: this.resolveApplicationId(),
        status: this.formData?.data?.application_status || this.sessionStorage.getItem('applicationStatus') || ''
      },
      config: this.config,
      features: {},
      readOnly: this.readOnly,
      currentSectionName,
      missingRequiredDocuments,
      hasMissingRequiredDocuments: missingRequiredDocuments.length > 0,
      canProceed: missingRequiredDocuments.length === 0,
      normalizeStatus: (value: any) => this.normalizeStatus(value),
      addDays: (value: any, days: number) => {
        const date = new Date(value);
        date.setDate(date.getDate() + days);
        return date;
      },
      today: new Date()
    };
  }

  private evaluateFeatureFlag(expression: string, fallback: boolean, featureAliases: string[] = [], row?: SupportingDocumentRow): boolean {
    const context = this.buildContext(row);
    const result = this.ruleService.evaluate<any>(expression, context, fallback);
    const normalizedResult = this.coerceBoolean(result);
    if (normalizedResult !== null) {
      return normalizedResult;
    }

    const aliases = featureAliases.length ? featureAliases : ['value'];
    for (const alias of aliases) {
      const normalizedAlias = String(alias || '').trim();
      if (!normalizedAlias) {
        continue;
      }

      const featureValue = this.coerceBoolean(context?.features?.[normalizedAlias]);
      if (featureValue !== null) {
        return featureValue;
      }

      const dataValue = this.coerceBoolean(context?.data?.[normalizedAlias]);
      if (dataValue !== null) {
        return dataValue;
      }
    }

    return fallback;
  }

  private coerceBoolean(value: any): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      if (value === 1) {
        return true;
      }
      if (value === 0) {
        return false;
      }
    }
    const normalized = String(value ?? '').trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return false;
    }
    return null;
  }

  private syncDynamicSourceRecordsFromFormData(): void {
    const sourceGridKey = String(this.config.dynamicDocuments.sourceGridKey || '').trim();
    if (!sourceGridKey) {
      this.dynamicSourceRecords = [];
      this.dynamicSourceKeys = new Set<string>();
      return;
    }

    const data = this.formData?.data || this.formData || {};
    const sourceValue = data?.[sourceGridKey];
    let rows: any[] = [];

    if (Array.isArray(sourceValue)) {
      rows = sourceValue;
    } else if (Array.isArray(sourceValue?.[sourceGridKey])) {
      rows = sourceValue[sourceGridKey];
    } else if (Array.isArray(sourceValue?.value?.[sourceGridKey])) {
      rows = sourceValue.value[sourceGridKey];
    }

    this.dynamicSourceRecords = rows.filter((row: any) => row && typeof row === 'object');
    this.dynamicSourceKeys = new Set(
      this.buildDynamicSourceRecordEntries().map((entry) => entry.sourceKey)
    );
    const filteredItems = data.applicationTimeLine.filter(
      (item: any) => item.cd_application_status === '18'
    );
    this.timelineIntakeStatusCount = filteredItems.length || 0;
    this.latestIntakeOccuredDate = filteredItems.length > 0 ?
      filteredItems.reduce((latest: any, current: any) =>
        new Date(current.occurred_date).getTime()  > new Date(latest).getTime()  ? current.occurred_date: latest,
        filteredItems[0].occurred_date // Start with the first item's date instead of an empty string
      )
      : null;

  }

  /**
   * Syncs additional dynamic type records from form data.
   * Reads rows from the configured source grid key, then groups the
   * sourceFieldName values by their categoryFieldName value. The resulting
   * map (`additionalDynamicTypesByCategory`) is used by `getUploadTypeOptions`
   * to inject extra document type options when the matching category is selected.
   */
  private syncAdditionalDynamicTypeRecords(): void {
    this.additionalDynamicTypeRecords = [];
    this.additionalDynamicTypesByCategory = {};

    if (!this.isAdditionalDynamicTypeEnabled()) {
      return;
    }

    const sourceGridKey = String(this.config?.additionalDynamicType?.sourceGridKey || '').trim();
    const sourceFieldName = String(this.config?.additionalDynamicType?.sourceFieldName || '').trim();
    const categoryFieldName = String(this.config?.additionalDynamicType?.categoryFieldName || '').trim();

    if (!sourceGridKey || !sourceFieldName) {
      return;
    }

    const data = this.formData?.data || this.formData || {};
    const sourceValue = data?.[sourceGridKey];
    let rows: any[] = [];

    if (Array.isArray(sourceValue)) {
      rows = sourceValue;
    } else if (Array.isArray(sourceValue?.[sourceGridKey])) {
      rows = sourceValue[sourceGridKey];
    } else if (Array.isArray(sourceValue?.value?.[sourceGridKey])) {
      rows = sourceValue.value[sourceGridKey];
    }

    this.additionalDynamicTypeRecords = rows.filter((row: any) => row && typeof row === 'object');

    const typesByCategory: Record<string, SupportingDocumentGridOption[]> = {};
    const seenByCategory: Record<string, Set<string>> = {};

    const addTypeToCategory = (categoryKey: string, typeValue: string) => {
      if (!typesByCategory[categoryKey]) {
        typesByCategory[categoryKey] = [];
        seenByCategory[categoryKey] = new Set<string>();
      }
      const dedupeKey = typeValue.toLowerCase();
      if (seenByCategory[categoryKey].has(dedupeKey)) {
        return;
      }
      seenByCategory[categoryKey].add(dedupeKey);
      typesByCategory[categoryKey].push({ key: typeValue, value: typeValue });
    };

    for (const record of this.additionalDynamicTypeRecords) {
      const typeValue = String(this.readFieldValueFromSource(sourceFieldName, record) || '').trim();
      if (!typeValue) {
        continue;
      }

      let rawCategoryValue = categoryFieldName
        ? String(this.readFieldValueFromSource(categoryFieldName, record) || '').trim()
        : '';

      // If the configured categoryFieldName doesn't exist as a field in the
      // record, treat it as a static category value (the user entered the
      // category name/key directly instead of a field path).
      if (!rawCategoryValue && categoryFieldName) {
        rawCategoryValue = categoryFieldName;
      }

      if (!rawCategoryValue) {
        addTypeToCategory('__all__', typeValue);
        continue;
      }

      // Resolve the category value against available category options so it
      // matches regardless of whether the grid stores a key, display name, or
      // lookup ID.
      const resolvedCategory = this.resolveCategoryOption(rawCategoryValue);

      // Store under all possible keys the category could be referenced by
      // in the upload dropdown.
      const storageKeys = new Set<string>();
      storageKeys.add(rawCategoryValue);
      if (resolvedCategory) {
        if (resolvedCategory.key) { storageKeys.add(resolvedCategory.key); }
        if (resolvedCategory.value) { storageKeys.add(resolvedCategory.value); }
        if (resolvedCategory.lookupId) { storageKeys.add(resolvedCategory.lookupId); }
      }

      for (const storageKey of storageKeys) {
        addTypeToCategory(storageKey, typeValue);
      }
    }

    this.additionalDynamicTypesByCategory = typesByCategory;
    // Invalidate merged options cache since additional types changed.
    this.cachedUploadTypeOptionsBase = null;
  }

  /**
   * Returns true when the Additional Dynamic Type feature is enabled.
   */
  private isAdditionalDynamicTypeEnabled(): boolean {
    const fromInput = this.isAdditionalDynamicTypeNeeded;
    const fromConfig = this.config?.additionalDynamicType?.enabled;
    const normalize = (value: any): boolean => {
      if (typeof value === 'boolean') {
        return value;
      }
      return String(value || '').trim().toLowerCase() === 'true';
    };
    return normalize(fromInput) || normalize(fromConfig);
  }

  /**
   * Returns additional dynamic type options for the given category.
   * Matches against the category key/value/lookupId to find the right set.
   */
  private getAdditionalDynamicTypesForCategory(category: string): SupportingDocumentGridOption[] {
    if (!this.isAdditionalDynamicTypeEnabled() || !category) {
      return [];
    }

    const normalizedCategory = this.normalizeValue(category);
    const categoryOption = this.resolveCategoryOption(category);

    // Try exact matches first (key, value, lookupId)
    const keysToTry = [
      category,
      categoryOption?.key,
      categoryOption?.value,
      categoryOption?.lookupId
    ].filter((item: any): item is string => !!item);

    for (const key of keysToTry) {
      if (this.additionalDynamicTypesByCategory[key]) {
        return this.additionalDynamicTypesByCategory[key];
      }
    }

    // Try normalized match
    for (const [mapKey, options] of Object.entries(this.additionalDynamicTypesByCategory)) {
      if (mapKey === '__all__') {
        continue;
      }
      if (this.normalizeValue(mapKey) === normalizedCategory) {
        return options;
      }
      // Also check if the mapKey matches any lookup property of the category option
      if (categoryOption && (
        this.isSameLookupValue(mapKey, categoryOption.key)
        || this.isSameLookupValue(mapKey, categoryOption.value)
        || this.isSameLookupValue(mapKey, categoryOption.lookupId)
      )) {
        return options;
      }
    }

    // If no per-category entries exist (all went to __all__), return them
    // for any category since filtering isn't possible.
    const hasCategorizedEntries = Object.keys(this.additionalDynamicTypesByCategory)
      .some((key: string) => key !== '__all__');

    if (!hasCategorizedEntries) {
      return this.additionalDynamicTypesByCategory['__all__'] || [];
    }

    // Per-category entries exist but none matched the selected category.
    return [];
  }

  private bindDynamicDocumentBridge(): void {
    const sourceGridKey = String(this.config?.dynamicDocuments?.sourceGridKey || '').trim();
    if (!sourceGridKey) {
      this.activeDynamicSourceGridKey = '';
      return;
    }
    if (sourceGridKey === this.activeDynamicSourceGridKey) {
      return;
    }

    this.activeDynamicSourceGridKey = sourceGridKey;
    this.dynamicDocumentBridge.watch(sourceGridKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: DynamicDocumentGridState | null) => {
        if (!state) {
          return;
        }
        if (state.sourceGridKey !== String(this.config?.dynamicDocuments?.sourceGridKey || '').trim()) {
          return;
        }
        if (String(state.operation || '').toLowerCase() === 'removeall') {
          void this.handleSourceGridRemoveAll(state);
          return;
        }
        this.setDynamicSourceRecords(state.rows);
        this.rebuildDerivedState();
      });
  }

  private async handleSourceGridRemoveAll(state: DynamicDocumentGridState): Promise<void> {
    const stateApplicationId = this.normalizeValue(state?.applicationId ?? '');
    const stateSectionName = this.normalizeValue(state?.sectionName ?? '');
    const currentApplicationId = this.normalizeValue(this.resolveApplicationId() || '');
    const currentSectionName = this.normalizeValue(this.getCurrentSectionName() || '');
    const targetApplicationId = stateApplicationId || currentApplicationId;
    const targetSectionName = stateSectionName || currentSectionName;

    if (!targetApplicationId || !targetSectionName) {
      this.setDynamicSourceRecords([]);
      this.rebuildDerivedState();
      return;
    }

    const rowsToDelete = (this.rawDocuments || []).filter((row: SupportingDocumentRow) => {
      if (!row || row.is_active === false) {
        return false;
      }
      if (this.normalizeStatus(row.status) === this.normalizeStatus(this.config.statuses.deleted)) {
        return false;
      }
      const rowApplicationId = this.normalizeValue(row.application_id ?? '');
      const rowSectionName = this.normalizeValue(row.section_name || this.resolveSectionName(row) || '');
      const applicationMatches = !rowApplicationId || rowApplicationId === targetApplicationId;
      const sectionMatches = rowSectionName === targetSectionName;
      return applicationMatches && sectionMatches && !!row.document_id;
    });

    if (!rowsToDelete.length) {
      this.setDynamicSourceRecords([]);
      this.rebuildDerivedState();
      return;
    }

    try {
      await Promise.all(rowsToDelete.map((row: SupportingDocumentRow) => this.persistDelete(row)));
      const deletedIds = new Set(rowsToDelete.map((row: SupportingDocumentRow) => String(row.document_id)));
      this.rawDocuments = this.rawDocuments.map((item: SupportingDocumentRow) => deletedIds.has(String(item.document_id))
        ? { ...item, status: this.config.statuses.deleted, is_active: false }
        : item
      );
      rowsToDelete.forEach((row: SupportingDocumentRow) => this.clearPlaceholderTypeSelectionForDeletedRow(row));
      this.setDynamicSourceRecords([]);
      this.rebuildDerivedState();
    } catch (error) {
      console.error('Unable to deactivate supporting documents on source grid removal.', error);
      this.alertService.error('Unable to remove related supporting documents.');
    }
  }

  private syncDynamicSourceRecordsFromBridge(): void {
    const sourceGridKey = String(this.config?.dynamicDocuments?.sourceGridKey || '').trim();
    if (!sourceGridKey) {
      return;
    }

    const rows = this.dynamicDocumentBridge.getRows(sourceGridKey);
    if (rows.length) {
      this.setDynamicSourceRecords(rows);
    }
  }

  private setDynamicSourceRecords(rows: any[]): void {
    this.dynamicSourceRecords = (Array.isArray(rows) ? rows : []).filter((row: any) => row && typeof row === 'object');
    this.dynamicSourceKeys = new Set(
      this.buildDynamicSourceRecordEntries().map((entry) => entry.sourceKey)
    );
  }

  private buildDynamicSourceRecordEntries(): Array<{ record: any; index: number; sourceKey: string }> {
    const occurrences: Record<string, number> = {};
    const rows: Array<{ record: any; index: number; sourceKey: string }> = [];

    this.dynamicSourceRecords.forEach((record: any, index: number) => {
      const baseSourceKey = this.resolveDynamicSourceKey(record, index);
      const occurrenceIndex = occurrences[baseSourceKey] || 0;
      occurrences[baseSourceKey] = occurrenceIndex + 1;
      const sourceKey = occurrenceIndex === 0 ? baseSourceKey : `${baseSourceKey}:duplicate:${occurrenceIndex}`;
      rows.push({ record, index, sourceKey });
    });

    return rows;
  }

  private resolveDynamicSourceKey(record: any, index: number): string {
    const sourceGridKey = String(this.config?.dynamicDocuments?.sourceGridKey || '').trim();
    const configuredField = String(this.config?.dynamicDocuments?.sourceRecordKeyField || '').trim();
    const candidateFields = [
      configuredField,
      'primary_id',
      'id',
      'state_id',
      'state',
      'key',
      'value'
    ].filter(Boolean);

    for (const field of candidateFields) {
      const value = this.readFieldValueFromSource(field, record);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return `${sourceGridKey}:${field}:${String(value).trim()}`;
      }
    }

    return `${sourceGridKey}:index:${index}`;
  }

  private getRowDynamicSourceKey(row?: SupportingDocumentRow | null): string {
    return String(row?._dynamicSourceKey || row?.dynamic_source_key || '').trim();
  }

  private getRowDynamicRuleId(row?: SupportingDocumentRow | null): string {
    return String(row?._dynamicRuleId || row?.dynamic_rule_id || '').trim();
  }

  private getRowDynamicTypeLookupRequestType(row?: SupportingDocumentRow | null): string {
    return String(
      row?._dynamicTypeLookupRequestType
      || row?.dynamic_type_lookup_request_type
      || this.config?.dynamicDocuments?.sourceTypeLookupRequestType
      || ''
    ).trim();
  }

  private resolveUploadDynamicTypeLookupRequestType(): string {
    const targetRequestType = this.getRowDynamicTypeLookupRequestType(this.uploadTargetRow);
    if (targetRequestType) {
      return targetRequestType;
    }

    const category = this.resolveUploadCategory();
    const docType = this.resolveUploadDocType();
    if (!category || !docType) {
      return '';
    }

    const rows: SupportingDocumentRow[] = [
      ...(this.visibleDocuments || []),
      ...(this.activeDocuments || []),
      ...(this.rawDocuments || [])
    ];
    const matchingRow = rows.find((row: SupportingDocumentRow) =>
      this.isSameLookupValue(row?.cd_document_category, category)
      && this.isSameLookupValue(row?.cd_document_type, docType)
      && !!this.getRowDynamicTypeLookupRequestType(row)
    );
    const rowRequestType = this.getRowDynamicTypeLookupRequestType(matchingRow);
    if (rowRequestType) {
      return rowRequestType;
    }

    const matchingRule = (this.dbRules || []).find((rule: SupportingDocumentRuleDefinition) =>
      rule?.is_active !== false
      && this.isRuleAppliedToCurrentGrid(rule)
      && this.isSameLookupValue(rule.cd_document_category, category)
      && !!String(rule.dynamic_type_lookup_category || '').trim()
    );

    return String(matchingRule?.dynamic_type_lookup_category || '').trim();
  }

  private getDynamicTypeLookupLabel(row: SupportingDocumentRow): string {
    const requestType = this.getRowDynamicTypeLookupRequestType(row);
    const typeValue = String(row?.cd_document_type || '').trim();
    if (!requestType || !typeValue) {
      return '';
    }

    this.ensureDynamicTypeLookupLoaded(requestType);
    const option = (this.dynamicTypeLookupOptions[requestType] || []).find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, typeValue) || this.isSameLookupValue(item.value, typeValue)
    );

    return option?.value || '';
  }

  /**
   * Resolves a type display label from already-cached lookups without
   * triggering async loads or change detection. Falls back to the raw value.
   */
  private resolveTypeLabelFromCache(row: SupportingDocumentRow, typeVal: string): string {
    // Try dynamic type lookup cache
    const requestType = this.getRowDynamicTypeLookupRequestType(row);
    if (requestType) {
      const dynamicMatch = (this.dynamicTypeLookupOptions[requestType] || []).find((item: SupportingDocumentGridOption) =>
        this.isSameLookupValue(item.key, typeVal) || this.isSameLookupValue(item.value, typeVal)
      );
      if (dynamicMatch?.value) return dynamicMatch.value;
    }
    // Try category type lookups
    const categoryTypes = this.getDocumentTypes(row?.cd_document_category || '');
    const catMatch = categoryTypes.find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, typeVal) || this.isSameLookupValue(item.value, typeVal)
    );
    if (catMatch?.value) return catMatch.value;
    if (requestType) return typeVal;
    // Try global document types
    const globalMatch = (this.lookupDocumentTypes || []).find((item: SupportingDocumentGridOption) =>
      this.isSameLookupValue(item.key, typeVal) || this.isSameLookupValue(item.value, typeVal)
    );
    if (globalMatch?.value) return globalMatch.value;
    return typeVal;
  }

  private ensureDynamicTypeLookupLoaded(requestType: string): void {
    const cacheKey = String(requestType || '').trim();
    if (!cacheKey || this.dynamicTypeLookupOptions[cacheKey]?.length || this.dynamicTypeLookupLoading[cacheKey]) {
      return;
    }

    this.dynamicTypeLookupLoading[cacheKey] = true;
    this.supportingDocumentRuleConfigService.getLookupOptionsByRequestType(cacheKey)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.dynamicTypeLookupLoading[cacheKey] = false;
        })
      )
      .subscribe({
        next: (options: SupportingDocumentGridOption[]) => {
          this.dynamicTypeLookupOptions[cacheKey] = options || [];
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.warn('Unable to load dynamic supporting document type lookup.', error);
        }
      });
  }

  private getMissingRequiredDocuments(): SupportingDocumentRow[] {
    const missing = this.activeDocuments.filter((row: SupportingDocumentRow) =>
      row.isPlaceholder && !this.hasUploadedFile(row) && !row._optionalRule
    );
    if (missing.length) {
      console.log('[DOC-GRID-DEBUG] Missing required documents:', missing.map((r: SupportingDocumentRow) => ({
        category: r.cd_document_category,
        type: r.cd_document_type,
        section: r.section_name,
        _placeholderKey: r._placeholderKey,
        _categoryIsAll: r._categoryIsAll,
        rule_id: r.rule_id,
        dynamicSourceKey: r.dynamic_source_key
      })));
    }
    return missing;
  }

  private resolveSectionName(row?: SupportingDocumentRow | null): string {
    if (row?.section_name) {
      return row.section_name;
    }
    if (this.config.ruleScope?.panelKey) {
      return String(this.config.ruleScope.panelKey || '');
    }
    return '';
  }

  private resolveEndpoint(template: string, row?: SupportingDocumentRow | null): string {
    return String(template || '')
      .replace('{documentId}', String(row?.document_id || ''))
      .replace('{applicationId}', String(this.resolveApplicationId() || ''))
      .replace('{personId}', String(this.resolvePersonId() || ''))
      .replace('{formId}', String(this.resolveEffectiveFormId() || ''))
      .replace('{responseId}', String(this.resolveResponseId() || ''))
      .replace('{sectionName}', encodeURIComponent(String(this.getCurrentSectionName() || '')))
      .replace('{pageId}', encodeURIComponent(String(this.config?.ruleScope?.pageId || this.pageId || this.resolveEffectiveFormId() || '')))
      .replace('{tabId}', encodeURIComponent(String(this.config?.ruleScope?.pageId || this.pageId || '')))
      .replace('{panelKey}', encodeURIComponent(String(this.config?.ruleScope?.panelKey || this.panelKey || '')))
      .replace('{stepperId}', encodeURIComponent(String(this.config?.ruleScope?.stepperId || this.stepperId || '3795')));
  }

  private resolveResponseId(): any {
    return this.formData?.data?.response_id
      || this.formData?.data?.id
      || this.formData?.submission?._id
      || this.formData?.submission?.id
      || this.value?.responseid
      || '';
  }

  private resolveEffectiveFormId(): any {
    return this.formId || this.gridId || this.formData?.formId || this.formData?.data?.formid || this.config.ruleScope?.pageId || this.pageId || '';
  }

  private resolveApplicationId(): any {
    return this.formData?.data?.application_id
      || this.sessionStorage.getItem('applicationID')
      || this.sessionStorage.getItem('applicationId')
      || '';
  }

  private resolvePersonId(): any {
    return this.formData?.data?.person_id
      || this.formData?.data?.personId
      || this.getSessionObject('personData')?.person_id
      || '';
  }

  private matchesShowAlwaysRoleCondition(rule: SupportingDocumentRuleDefinition): boolean {
    const configuredRoleId = String(rule.condition_json?.show_always_role_id ?? '').trim();
    if (!configuredRoleId) {
      return true;
    }
    const roleMatch = rule.condition_json?.show_always_role_match === true || rule.condition_json?.show_always_role_match === 'true';
    const currentRoleId = String(this.sessionStorage.getItem('role_id') ?? '').trim();
    const currentRoleMatchesConfigured = currentRoleId === configuredRoleId;
    return roleMatch ? currentRoleMatchesConfigured : !currentRoleMatchesConfigured;
  }

  private resolveCurrentUserRoleId(): number | string | null {
    const data = this.formData?.data || this.formData || {};
    const roleId =
      data?.user?.role_id
      ?? data?.user?.roleId
      ?? data?.currentUser?.role_id
      ?? data?.currentUser?.roleId
      ?? data?.role_id
      ?? data?.roleId
      ?? data?.user_role_id
      ?? data?.userRoleId
      ?? this.sessionStorage.getItem('role_id')
      ?? this.getSessionObject('user')?.role_id
      ?? this.getSessionObject('user')?.roleId;

    const normalizedRoleId = String(roleId ?? '').trim();
    if (!normalizedRoleId) {
      return null;
    }

    return /^\d+$/.test(normalizedRoleId) ? Number(normalizedRoleId) : normalizedRoleId;
  }

  private resolveUserDisplayName(): string {
    const user = this.getSessionObject('user');
    const personData = this.getSessionObject('personData');
    const first = user?.firstname || personData?.first_name || '';
    const last = user?.lastname || personData?.last_name || '';
    const fullName = `${first} ${last}`.trim();
    return fullName || user?.username || this.sessionStorage.getItem('username') || 'Current User';
  }

  private getSessionObject(key: string): any {
    const value = this.sessionStorage.getItem(key);
    if (!value) {
      return {};
    }
    if (typeof value === 'object') {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }

  private isArchived(row: SupportingDocumentRow): boolean {
    const archivedStatuses = (this.config.archive.archivedStatuses || []).map((item: string) => this.normalizeStatus(item));
    return archivedStatuses.includes(this.normalizeStatus(row.status));
  }

  private getCurrentSectionName(): string {
    return String(this.resolveSectionName() || '').trim();
  }

  private shouldIncludeRowForSection(row: SupportingDocumentRow, currentSectionName: string): boolean {
    if (this.isShowOnlyAvailableDocumentsMode()) {
      return true;
    }
    const normalizedCurrentSection = this.normalizeValue(currentSectionName);
    if (!normalizedCurrentSection) {
      return true;
    }
    return this.normalizeValue(row?.section_name || '') === normalizedCurrentSection;
  }

  private resolveListEndpoint(): string {
    const configuredEndpoint = this.config?.api?.listEndpoint || this.listEndpoint || '';
    if (!configuredEndpoint) {
      return '';
    }

    return this.resolveEndpoint(configuredEndpoint);
  }

  private hasMatchingDocumentRequirement(
    rows: SupportingDocumentRow[],
    category: string,
    typeValue: string,
    sectionName: string
  ): boolean {
    return !!this.findMatchingDocumentRequirement(rows, category, typeValue, sectionName);
  }

  private findMatchingDocumentRequirement(
    rows: SupportingDocumentRow[],
    category: string,
    typeValue: string,
    sectionName: string
  ): SupportingDocumentRow | undefined {
    return (rows || []).find((row: SupportingDocumentRow) => {
      if (!this.hasUploadedFile(row)) {
        return false;
      }

      const categoryMatches = this.normalizeValue(row.cd_document_category) === this.normalizeValue(category);
      const sectionMatches = this.normalizeValue(row.section_name || '') === this.normalizeValue(sectionName || '');
      if (!categoryMatches || !sectionMatches) {
        return false;
      }

      if (!this.normalizeValue(typeValue)) {
        return true;
      }

      return this.normalizeValue(row.cd_document_type || '') === this.normalizeValue(typeValue);
    });
  }

  private hasPendingUploadChanges(): boolean {
    return !!this.selectedFile
      || this.normalizeValue(this.uploadDraft.category) !== this.normalizeValue(this.initialUploadDraft.category)
      || this.normalizeValue(this.uploadDraft.docType) !== this.normalizeValue(this.initialUploadDraft.docType)
      || this.normalizeValue(this.uploadDraft.sectionName) !== this.normalizeValue(this.initialUploadDraft.sectionName);
  }

  private resetUploadDraftState(): void {
    this.uploadError = '';
    this.uploadCategoryError = '';
    this.uploadTypeError = '';
    this.selectedFile = null;
    this.uploadTargetRow = null;
    this.uploadMode = 'create';
    this.uploadDraft = { category: '', docType: '', sectionName: '' };
    this.initialUploadDraft = { category: '', docType: '', sectionName: '' };
    this.currentUploadTypes = [];
  }

  private async performUpload(modal: NgbModalRef): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    try {
      const wasReplaceFlow = this.uploadMode === 'replace';
      const uploaded = await this.uploadFile(this.selectedFile);
      const payload = this.buildDocumentPayload(uploaded);
      let persisted = await this.persistDocument(payload, this.uploadTargetRow);
      persisted = await this.hydrateReplacementDocumentId(persisted);
      this.upsertRow(persisted, this.uploadTargetRow);
      this.rebuildDerivedState();
      this.resetUploadDraftState();
      modal.close();
      this.alertService.success(wasReplaceFlow ? 'Replacement uploaded successfully.' : 'Document uploaded successfully.');
      window.dispatchEvent(new CustomEvent('supporting-document-uploaded', {
        detail: { category: payload.cd_document_category, type: payload.cd_document_type }
      }));
    } catch (error) {
      console.error(error);
      this.uploadError = 'Upload failed. Please try again.';
    }
  }

  private isSameRow(left: SupportingDocumentRow, right: SupportingDocumentRow): boolean {
    if (left.document_id && right.document_id) {
      return String(left.document_id) === String(right.document_id);
    }
    if (left._placeholderKey && right._placeholderKey) {
      return left._placeholderKey === right._placeholderKey;
    }
    return this.normalizeValue(left.file_name) === this.normalizeValue(right.file_name)
      && this.normalizeValue(left.cd_document_category) === this.normalizeValue(right.cd_document_category)
      && this.normalizeValue(left.cd_document_type) === this.normalizeValue(right.cd_document_type)
      && this.normalizeValue(left.section_name) === this.normalizeValue(right.section_name);
  }

  private normalizeStatus(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private normalizeValue(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private loadLookupMessages(): void {
    this.errorMessage = this.readStoredLookupMap('errorMessage');
    this.placeholderMessage = this.readStoredLookupMap('placeholderMessage');
  }

  private readStoredLookupMap(key: string): Record<string, string> {
    const storedValue = this.sessionStorage.getItem(key) || window.localStorage.getItem(key) || '{}';
    if (typeof storedValue === 'object') {
      return storedValue || {};
    }
    try {
      const parsed = JSON.parse(storedValue);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private extractLookupRows(response: any): any[] {
    if (Array.isArray(response?.data?.data)) {
      return response.data.data;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response)) {
      return response;
    }
    return [];
  }

  private normalizeLookupOption(item: any): SupportingDocumentGridOption {
    const key = String(item?.key ?? item?.value ?? item?.id ?? '').trim();
    const value = String(item?.value ?? item?.label ?? item?.name ?? item?.key ?? '').trim();
    const lookupId = String(item?.id ?? item?.lookupid ?? item?.lookup_id ?? key).trim();
    const parentIdValue = item?.parentid ?? item?.parentId ?? item?.parent_id ?? '';
    const parentId = String(parentIdValue ?? '').trim();
    return {
      key,
      value,
      lookupId,
      parentId,
      optionaldata: this.parseLookupOptionalData(item?.optionaldata ?? item?.optional_data ?? item?.optionalData),
      lookup: Array.isArray(item?.lookup)
        ? item.lookup
          .map((child: any) => this.normalizeLookupOption({
            ...child,
            parentid: child?.parentid ?? child?.parentId ?? child?.parent_id ?? lookupId
          }))
          .filter((child: SupportingDocumentGridOption) => !!child.key && !!child.value)
        : []
    };
  }

  private parseLookupOptionalData(optionaldata: any): any {
    if (!optionaldata) {
      return null;
    }
    if (typeof optionaldata === 'object') {
      return optionaldata;
    }
    if (typeof optionaldata !== 'string') {
      return null;
    }
    try {
      return JSON.parse(optionaldata);
    } catch {
      return null;
    }
  }

  private matchesCategorySection(item: SupportingDocumentGridOption, useAdditionalSection = false): boolean {
    const panelKey = this.getCurrentSectionName();
    const optionalData = item?.optionaldata || {};
    if (!panelKey) {
      return this.matchesCategoryFormDataFilters(optionalData);
    }
    const sectionName = String(optionalData?.section_name || optionalData?.sectionName || '').trim();
    const additionalSection = String(optionalData?.additional_section || optionalData?.additionalSection || '').trim();
    const sectionMatches = this.normalizeValue(sectionName) === this.normalizeValue(panelKey);
    const additionalSectionMatches = this.normalizeValue(additionalSection) === this.normalizeValue(panelKey);
    return (sectionMatches || (useAdditionalSection && additionalSectionMatches))
      && this.matchesCategoryFormDataFilters(optionalData);
  }

  private isAdditionalCategoryRule(rule: SupportingDocumentRuleDefinition): boolean {
    const value = rule?.show_additional_category ?? (rule as any)?.showAdditionalCategory;
    return this.coerceBooleanFlag(value);
  }

  private shouldUseAdditionalCategoriesForUpload(): boolean {
    if (this.uploadTargetRow?._showAdditionalCategory) {
      return true;
    }
    return (this.dbRules || []).some((rule: SupportingDocumentRuleDefinition) =>
      rule?.is_active !== false
      && this.isAdditionalCategoryRule(rule)
      && this.isRuleAppliedToCurrentGrid(rule)
    );
  }

  private isRuleAppliedToCurrentGrid(rule: SupportingDocumentRuleDefinition): boolean {
    if (!this.ruleMatchesScope(rule)) {
      return false;
    }

    const hasCondition = this.hasRuleCondition(rule.condition_json);
    const conditionMatched = !hasCondition || this.evaluateConditionJson(rule.condition_json);

    if (rule.show_always_default && this.matchesShowAlwaysRoleCondition(rule)) {
      return !hasCondition || conditionMatched;
    }

    return conditionMatched;
  }

  private matchesCategoryFormDataFilters(optionalData: any): boolean {
    return this.matchesCategoryFormDataFilter(optionalData, 'cd_application_type')
      && this.matchesCategoryFormDataFilter(optionalData, 'license_path');
  }

  private matchesCategoryFormDataFilter(optionalData: any, fieldName: string): boolean {
    const configuredValues = this.toFilterValues(optionalData?.[fieldName]);
    if (!configuredValues.length) {
      return true;
    }

    const formValues = this.toFilterValues(this.readFieldValue(fieldName));
    if (!formValues.length) {
      return false;
    }

    return formValues.some((value: string) => configuredValues.includes(value));
  }

  private toFilterValues(value: any): string[] {
    if (value === null || value === undefined || value === '') {
      return [];
    }
    if (Array.isArray(value)) {
      return value
        .map((item: any) => this.normalizeValue(item))
        .filter((item: string) => !!item);
    }
    return [this.normalizeValue(value)].filter((item: string) => !!item);
  }

  private isRootLookupOption(item: SupportingDocumentGridOption): boolean {
    const parentId = String(item?.parentId || '').trim();
    return !parentId || parentId === '0';
  }

  private resolveUploadCategory(): string {
    return this.uploadDraft.category || this.uploadTargetRow?.cd_document_category || '';
  }

  private resolveUploadDocType(): string {
    return this.uploadDraft.docType || this.uploadTargetRow?.cd_document_type || '';
  }

  private getCategoryRequiredMessage(): string {
    return this.errorMessage['M0048']
      || this.errorMessage['M048']
      || this.config.validation.categoryRequiredMessage
      || 'Category is required.';
  }

  private getTypeRequiredMessage(): string {
    return this.errorMessage['M0049']
      || this.errorMessage['M049']
      || this.config.validation.typeRequiredMessage
      || 'Type is required.';
  }

  private ensureCategoryTypesLoaded(category: string): void {
    const categoryOption = this.resolveCategoryOption(category);
    const categoryKey = String(categoryOption?.key || category || '').trim();
    const parentId = String(categoryOption?.lookupId || '').trim();
    const cacheKey = `${categoryKey}::${parentId}`;
    if (!categoryOption || !categoryKey || !parentId || Array.isArray(categoryOption.lookup) && categoryOption.lookup.length) {
      return;
    }
    if (this.typeLoadInProgress[cacheKey]) {
      return;
    }

    this.typeLoadInProgress[cacheKey] = true;
    this.supportingDocumentRuleConfigService.getSupportingDocumentTypesForCategory(categoryOption, parentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.typeLoadInProgress[cacheKey] = false;
        })
      )
      .subscribe({
        next: (types: SupportingDocumentGridOption[]) => {
          categoryOption.lookup = this.sortDocumentTypeOptions(types);
          if (parentId) {
            this.lookupTypesByParentId[parentId] = this.sortDocumentTypeOptions(types);
          }
          this.lookupDocumentTypes = this.sortDocumentTypeOptions(this.lookupCategories.flatMap((item: SupportingDocumentGridOption) => item.lookup || []));
          this.syncUploadTypes(this.resolveUploadCategory(), true);
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.warn('Unable to load supporting document types for category.', error);
        }
      });
  }

  private syncUploadTypes(category: string, preserveCurrentSelection = false): void {
    // Invalidate merged options cache since inputs are changing.
    this.cachedUploadTypeOptionsBase = null;

    // "All Categories" rule: keep the type list independent of the picked
    // category and never auto-select for the user, so they can freely switch
    // between any of the configured types.
    const isAllCategoryRule = this.hasAllCategoryActiveRule();

    // If the target row already has a document type set (e.g. dynamic type from
    // field), preserve that value unconditionally — it won't exist in the
    // category's regular type options but is still valid.
    const rowType = String(this.uploadTargetRow?.cd_document_type || '').trim();
    if (rowType && this.uploadDraft.docType === rowType) {
      this.currentUploadTypes = this.sortDocumentTypeOptions(this.getDocumentTypes(category));
      return;
    }

    this.currentUploadTypes = this.sortDocumentTypeOptions(this.getDocumentTypes(category));

    // Include dynamic type values from rows matching this category. Rule-based
    // dynamic types can live on a placeholder before upload, then on the saved
    // document row after inline upload; both should remain available for
    // additional uploads in the same category.
    if (category || isAllCategoryRule) {
      const rows: SupportingDocumentRow[] = [
        ...(this.visibleDocuments || []),
        ...(this.activeDocuments || []),
        ...(this.rawDocuments || [])
      ];
      const seen = new Set<string>(this.currentUploadTypes.map((t: SupportingDocumentGridOption) => t.key.toLowerCase()));
      for (const row of rows) {
        if (!isAllCategoryRule) {
          const rowCat = String(row?.cd_document_category || '').trim().toLowerCase();
          if (rowCat !== String(category || '').trim().toLowerCase()) continue;
        }
        const typeVal = String(row?.cd_document_type || '').trim();
        if (!typeVal || seen.has(typeVal.toLowerCase())) continue;
        seen.add(typeVal.toLowerCase());
        const label = this.resolveTypeLabelFromCache(row, typeVal);
        this.currentUploadTypes = [...this.currentUploadTypes, { key: typeVal, value: label } as SupportingDocumentGridOption];
      }
    }

    // When additional dynamic types are enabled, include them in the effective
    // type list so the current selection isn't incorrectly cleared.
    const additionalTypes = this.getAdditionalDynamicTypesForCategory(category);
    const effectiveTypes = [...this.currentUploadTypes, ...additionalTypes];

    if (!category || !effectiveTypes.length) {
      this.uploadDraft.docType = '';
      return;
    }
    const hasCurrentSelection = effectiveTypes.some((type: SupportingDocumentGridOption) =>
      this.isSameLookupValue(type.key, this.uploadDraft.docType) || this.isSameLookupValue(type.value, this.uploadDraft.docType)
    );
    // For the All-Categories rule, never auto-select the type or reset it on
    // category change — the user must always be able to pick any type.
    if (isAllCategoryRule) {
      if (!hasCurrentSelection) {
        this.uploadDraft.docType = '';
      }
      return;
    }
    if (effectiveTypes.length === 1) {
      this.uploadDraft.docType = effectiveTypes[0].key;
      return;
    }
    if (!preserveCurrentSelection || !hasCurrentSelection) {
      this.uploadDraft.docType = '';
    }
  }

  private syncUploadCategorySelection(preserveCurrentSelection = false): void {
    if (!this.shouldShowUploadCategorySelector()) {
      return;
    }

    const categories = this.getAvailableCategories();
    if (!categories.length) {
      return;
    }

    const hasCurrentSelection = categories.some((category: SupportingDocumentGridOption) =>
      this.isSameLookupValue(category.key, this.uploadDraft.category) || this.isSameLookupValue(category.value, this.uploadDraft.category)
    );

    if (categories.length === 1) {
      this.uploadDraft.category = categories[0].key;
      return;
    }

    if (!preserveCurrentSelection || !hasCurrentSelection) {
      this.uploadDraft.category = '';
    }
  }

  private matchesActiveDbRule(row: SupportingDocumentRow): boolean {
    return this.dbRules
      .filter((rule: SupportingDocumentRuleDefinition) => this.ruleMatchesScope(rule))
      .some((rule: SupportingDocumentRuleDefinition) => {
        const hasCondition = this.hasRuleCondition(rule.condition_json);
        const conditionMatched = !hasCondition || this.evaluateConditionJson(rule.condition_json);
        // Optional rules only mark a row as required when an explicit condition
        // is defined AND currently matches.
        if (rule.show_always_default && this.matchesShowAlwaysRoleCondition(rule)) {
          if (!hasCondition || !conditionMatched) {
            return false;
          }
        } else if (!conditionMatched) {
          return false;
        }
        const rawCategory = String(rule.cd_document_category || '').trim();
        // ALL-category rules match a row by rule_id + section instead of by
        // category, since the user picks the specific category at upload time.
        // Fallback: match by _placeholderKey or by type+section for rows where rule_id was not persisted.
        if (rawCategory.toUpperCase() === 'ALL') {
          const sectionMatches = this.normalizeValue(row?.section_name || '') === this.normalizeValue(this.resolveSectionName() || '');
          if (rule.rule_id && String(row?.rule_id || '') === String(rule.rule_id)) {
            return sectionMatches;
          }
          if (row?._placeholderKey) {
            const ruleIndex = this.dbRules.filter((r: SupportingDocumentRuleDefinition) => this.ruleMatchesScope(r)).indexOf(rule);
            const expectedKey = `db-${rule.rule_id || ruleIndex}-${rawCategory}-${String(rule.cd_document_type || '').trim()}-${this.resolveSectionName() || ''}`;
            if (row._placeholderKey === expectedKey) {
              return true;
            }
          }
          const ruleType = String(rule.cd_document_type || '').trim();
          if (ruleType && sectionMatches) {
            return this.normalizeValue(row?.cd_document_type || '') === this.normalizeValue(ruleType);
          }
          return false;
        }
        return this.rowMatchesRequirement(
          row,
          rawCategory,
          String(rule.cd_document_type || '').trim(),
          this.resolveSectionName()
        );
      });
  }

  private matchesActiveConfiguredRequirement(row: SupportingDocumentRow): boolean {
    return (this.config.requirements || []).some((requirement: SupportingDocumentGridRequirement) => {
      const shouldShow = this.evaluateBoolean(requirement.showWhen || requirement.requiredWhen || 'true', true);
      if (!shouldShow) {
        return false;
      }
      const sectionName = requirement.sectionNameExpression
        ? String(this.evaluateValue(requirement.sectionNameExpression, '') || '')
        : this.resolveSectionName();
      const types = this.resolveRequirementTypes(requirement);
      if (!types.length) {
        return this.rowMatchesRequirement(row, requirement.category, '', sectionName);
      }
      return types.some((typeValue: string) => this.rowMatchesRequirement(row, requirement.category, typeValue, sectionName));
    });
  }

  private matchesActiveDependentRequirement(row: SupportingDocumentRow): boolean {
    if (this.isDependentPlaceholderRow(row)) {
      return true;
    }

    return (this.activeDocuments || []).some((sourceRow: SupportingDocumentRow) => {
      if (!sourceRow || this.isSameRow(sourceRow, row)) {
        return false;
      }

      return this.getDependentRequirementsForRow(sourceRow).some((dependency: SupportingDocumentDependentRequirement) =>
        this.rowMatchesRequirement(
          row,
          dependency.category || '',
          dependency.type || '',
          String(dependency.sectionName ?? sourceRow.section_name ?? this.resolveSectionName(sourceRow) ?? '').trim()
        )
      );
    });
  }

  private isDependentPlaceholderRow(row: SupportingDocumentRow | null | undefined): boolean {
    return !!row?.isPlaceholder && String(row?._placeholderKey || '').startsWith('dependent-');
  }

  private isRequiredPlaceholderRow(row: SupportingDocumentRow | null | undefined): boolean {
    return !!row?._placeholderKey && !row?._optionalRule;
  }

  private rowMatchesRequirement(row: SupportingDocumentRow, category: string, typeValue: string, sectionName: string): boolean {
    const categoryMatches = this.normalizeValue(row?.cd_document_category) === this.normalizeValue(category);
    const sectionMatches = this.normalizeValue(row?.section_name || '') === this.normalizeValue(sectionName || '');
    if (!categoryMatches || !sectionMatches) {
      return false;
    }
    if (!this.normalizeValue(typeValue)) {
      return true;
    }
    return this.normalizeValue(row?.cd_document_type || '') === this.normalizeValue(typeValue);
  }

  private ruleMatchesScope(rule: SupportingDocumentRuleDefinition): boolean {
    const stepperId = this.config?.ruleScope?.stepperId || this.stepperId || this.resolveEffectiveFormId() || '';
    const pageId = this.config?.ruleScope?.pageId || this.pageId || '';
    const panelKey = this.config?.ruleScope?.panelKey || this.panelKey || '';

    const pageMatches = !stepperId || String(rule.stepper_id || '') === String(stepperId);
    const tabMatches = !pageId || String(rule.page_id || '') === String(pageId);
    const panelMatches = !panelKey || String(rule.panel_key || '') === String(panelKey);

    return pageMatches && tabMatches && panelMatches;
  }

  private evaluateConditionJson(condition: any): boolean {
    if (!this.hasRuleCondition(condition)) {
      return true;
    }
    if (typeof condition === 'string') {
      try {
        return this.evaluateConditionJson(JSON.parse(condition));
      } catch {
        return false;
      }
    }

    if (Array.isArray(condition)) {
      const validConditions = condition.filter((item: any) => item && typeof item === 'object');
      if (!validConditions.length) {
        return false;
      }

      return validConditions.reduce((result: boolean, item: any, index: number) => {
        const current = this.evaluateConditionJson({
          field: item.field,
          operator: item.operator,
          value: item.value
        });
        if (index === 0) {
          return current;
        }
        const combineOperator = String(item.combineOperator || 'AND').trim().toUpperCase();
        return combineOperator === 'OR' ? (result || current) : (result && current);
      }, false);
    }

    if (Array.isArray(condition?.rules) && condition.logicalOperator) {
      const op = String(condition.logicalOperator).trim().toUpperCase();
      const results = condition.rules.map((rule: any) => this.evaluateConditionJson(rule));
      return op === 'OR' ? results.some(Boolean) : results.every(Boolean);
    }

    const field = String(condition.field || '').trim();
    const operator = String(condition.operator || '').trim();
    const expectedValue = condition.value;
    const actualValue = this.readFieldValue(field);

    switch (operator) {
      case 'equals':
        return actualValue == expectedValue;
      case 'notEquals':
        return actualValue != expectedValue;
      case 'contains':
        return String(actualValue ?? '').includes(String(expectedValue ?? ''));
      case 'in':
        return Array.isArray(expectedValue) && expectedValue.includes(actualValue);
      case 'notIn':
        return Array.isArray(expectedValue) && !expectedValue.includes(actualValue);
      case 'isTrue':
        return actualValue === true;
      case 'isFalse':
        return actualValue === false;
      case 'isEmpty':
        return actualValue === null || actualValue === undefined || String(actualValue).trim() === '';
      case 'isNotEmpty':
        return !(actualValue === null || actualValue === undefined || String(actualValue).trim() === '');
      default:
        return false;
    }
  }

  private evaluateConditionJsonForSource(condition: any, source: any): boolean {
    if (!this.hasRuleCondition(condition)) {
      return true;
    }
    if (typeof condition === 'string') {
      try {
        return this.evaluateConditionJsonForSource(JSON.parse(condition), source);
      } catch {
        return false;
      }
    }

    if (Array.isArray(condition)) {
      const validConditions = condition.filter((item: any) => item && typeof item === 'object');
      if (!validConditions.length) {
        return false;
      }

      return validConditions.reduce((result: boolean, item: any, index: number) => {
        const current = this.evaluateConditionJsonForSource({
          field: item.field,
          operator: item.operator,
          value: item.value
        }, source);
        if (index === 0) {
          return current;
        }
        const combineOperator = String(item.combineOperator || 'AND').trim().toUpperCase();
        return combineOperator === 'OR' ? (result || current) : (result && current);
      }, false);
    }

    if (Array.isArray(condition?.rules) && condition.logicalOperator) {
      const op = String(condition.logicalOperator).trim().toUpperCase();
      const results = condition.rules.map((rule: any) => this.evaluateConditionJsonForSource(rule, source));
      return op === 'OR' ? results.some(Boolean) : results.every(Boolean);
    }

    const field = String(condition.field || '').trim();
    const operator = String(condition.operator || '').trim();
    const expectedValue = condition.value;
    const actualValue = this.readFieldValueFromSource(field, source);

    switch (operator) {
      case 'equals':
        return actualValue == expectedValue;
      case 'notEquals':
        return actualValue != expectedValue;
      case 'contains':
        return String(actualValue ?? '').includes(String(expectedValue ?? ''));
      case 'in':
        return Array.isArray(expectedValue) && expectedValue.includes(actualValue);
      case 'notIn':
        return Array.isArray(expectedValue) && !expectedValue.includes(actualValue);
      case 'isTrue':
        return actualValue === true;
      case 'isFalse':
        return actualValue === false;
      case 'isEmpty':
        return actualValue === null || actualValue === undefined || String(actualValue).trim() === '';
      case 'isNotEmpty':
        return !(actualValue === null || actualValue === undefined || String(actualValue).trim() === '');
      default:
        return false;
    }
  }

  private hasRuleCondition(condition: any): boolean {
    if (condition === null || condition === undefined) {
      return false;
    }
    if (typeof condition === 'string') {
      return String(condition).trim() !== '';
    }
    if (typeof condition === 'object') {
      // Ignore metadata keys (e.g. is_not_required, show_always_role_id) when checking for actual conditions
      const keys = Object.keys(condition).filter((k: string) => !['is_not_required', 'show_always_role_id', 'show_always_role_match'].includes(k));
      return keys.length > 0;
    }
    return true;
  }

  private readFieldValue(fieldPath: string): any {
    if (!fieldPath) {
      return undefined;
    }
    const source = this.formData?.data || this.formData || {};
    return String(fieldPath).split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }
      return acc[key];
    }, source);
  }

  private readFieldValueFromSource(fieldPath: string, source: any): any {
    if (!fieldPath) {
      return undefined;
    }
    return String(fieldPath).split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }
      return acc[key];
    }, source || {});
  }

  private buildComponentValue(): SupportingDocumentGridValue {
    const emittedValue = this.rawDocuments.map((row: SupportingDocumentRow) => ({ ...row }));
    const currentSectionName = this.getCurrentSectionName();

    // In show-only or read-only mode, always allow navigation — no validation.
    if (this.isShowOnlyAvailableDocumentsMode() || this.readOnly) {
      return {
        documents: emittedValue,
        activeDocuments: this.activeDocuments.map((row: SupportingDocumentRow) => ({ ...row })),
        archivedDocuments: this.archivedDocuments.map((row: SupportingDocumentRow) => ({ ...row })),
        currentSectionName,
        missingRequiredDocuments: [],
        hasMissingRequiredDocuments: false,
        hasMissedRequiredDocuments: false,
        showMissingRequiredDocumentsError: false,
        canProceed: true
      };
    }

    const missingRequiredDocuments = this.getMissingRequiredDocuments();
    return {
      documents: emittedValue,
      activeDocuments: this.activeDocuments.map((row: SupportingDocumentRow) => ({ ...row })),
      archivedDocuments: this.archivedDocuments.map((row: SupportingDocumentRow) => ({ ...row })),
      currentSectionName,
      missingRequiredDocuments: missingRequiredDocuments.map((row: SupportingDocumentRow) => ({ ...row })),
      hasMissingRequiredDocuments: missingRequiredDocuments.length > 0,
      hasMissedRequiredDocuments: missingRequiredDocuments.length > 0,
      showMissingRequiredDocumentsError: this.showMissingRequiredDocumentsError,
      canProceed: missingRequiredDocuments.length === 0
    };
  }

  private emitValue(): void {
    const componentValue = this.buildComponentValue();
    this.value = componentValue;
    this.valueChange.emit(componentValue);
    this.formioEvent.emit({
      eventName: 'supportingDocuments',
      data: {
        ...componentValue,
        value: componentValue.documents
      }
    });
  }

  private syncNavigationValidationHandlers(): void {
    // Do not attach navigation validation when in show-only or read-only mode,
    // or when the show-only condition hides the component.
    if (this.isShowOnlyAvailableDocumentsMode() || this.readOnly) {
      this.clearNavigationValidationHandlers();
      return;
    }
    if (this.isShowOnlyConditionActive() && !this.evaluateShowOnlyCondition()) {
      this.clearNavigationValidationHandlers();
      return;
    }

    try {
      this.clearNavigationValidationTimers();
      const syncHandlers = () => {
        const buttons = this.getWorkflowNavigationButtons();

        Array.from(this.navigationButtonsWithValidation).forEach((button: HTMLButtonElement) => {
          if (!buttons.includes(button) || !document.body.contains(button)) {
            button.removeEventListener('click', this.handleNavigationValidationClick, true);
            this.navigationButtonsWithValidation.delete(button);
          }
        });

        buttons.forEach((button: HTMLButtonElement) => {
          if (!this.navigationButtonsWithValidation.has(button)) {
            button.addEventListener('click', this.handleNavigationValidationClick, true);
            this.navigationButtonsWithValidation.add(button);
          }
        });
      };

      syncHandlers();
      [150, 450, 900].forEach((delay: number) => {
        const timerId = window.setTimeout(syncHandlers, delay);
        this.navigationValidationSyncTimers.push(timerId);
      });
    } catch (error) {      console.error('Failed to bind supporting document navigation validation', error);
    }
  }

  private validateRequiredDocumentsBeforeNavigation(event: Event): void {
    // Skip all validation when in show-only/read-only mode or when the
    // show-only condition hides the component — user should be able to
    // navigate freely without any document validation blocking.
    if (this.isShowOnlyAvailableDocumentsMode() || this.readOnly) {
      return;
    }
    if (this.isShowOnlyConditionActive() && !this.evaluateShowOnlyCondition()) {
      return;
    }

    if (this.getMissingRequiredDocuments().length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showMissingRequiredDocumentsError = true;
      this.alertService.error('Missing required information. Complete all fields and documents to continue.');
      this.emitValue();
      return;
    }

    if (this.hasPendingReviewerDocumentStatuses()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.alertService.error('Please mark all documents as Verified or Deficient.');
      this.emitValue();
    }
  }

  private hasPendingReviewerDocumentStatuses(): boolean {
    if (this.isCaseDocMode()) {
      return false;
    }
    if (!this.shouldValidateReviewerDocumentStatuses()) {
      return false;
    }

    return this.activeDocuments
      .filter((row: SupportingDocumentRow) => this.hasUploadedFile(row))
      .some((row: SupportingDocumentRow) => !this.isReviewedDocumentStatus(row.status));
  }

  private shouldValidateReviewerDocumentStatuses(): boolean {
    const roleId = this.resolveCurrentUserRoleId();
    if (String(roleId || '').trim() === '405') {
      return false;
    }

    if (roleId !== null && roleId !== undefined && String(roleId).trim() !== '') {
      return true;
    }

    return this.activeDocuments.some((row: SupportingDocumentRow) =>
      this.hasUploadedFile(row) && (this.canMarkValid(row) || this.canMarkDeficient(row))
    );
  }

  private isReviewedDocumentStatus(status: any): boolean {
    const normalizedStatus = this.normalizeStatus(status);
    const allowedStatuses = [
      this.config.statuses.validated,
      this.resolvedDocumentStatuses?.validated,
      'Verified',
      'Validated',
      'Valid',
      this.config.statuses.deficient,
      this.resolvedDocumentStatuses?.deficient,
      'Deficient',
      this.config.statuses.case,
      'Case'
    ].map((item: any) => this.normalizeStatus(item));

    return allowedStatuses.includes(normalizedStatus);
  }

  /**
   * Returns true when the Case Document Mode is enabled via the formio checkbox.
   * In this mode all uploaded documents get status 'Case' and reviewer
   * verification actions (Mark Valid, Mark Deficient, View Deficiency) are hidden.
   */
  private isCaseDocMode(): boolean {
    const value = this.caseDocEnabled;
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  private clearNavigationValidationHandlers(): void {
    this.clearNavigationValidationTimers();
    this.navigationButtonsWithValidation.forEach((button: HTMLButtonElement) => {
      button.removeEventListener('click', this.handleNavigationValidationClick, true);
    });
    this.navigationButtonsWithValidation.clear();
  }

  private clearNavigationValidationTimers(): void {
    this.navigationValidationSyncTimers.forEach((timerId: number) => window.clearTimeout(timerId));
    this.navigationValidationSyncTimers = [];
  }

  private getWorkflowNavigationButtons(): HTMLButtonElement[] {
    const selector = [
      '#assessmentForm button',
      '.formio-form button',
      '.formio-component-form button',
      'button[name="data[submit]"]',
      'button.btn-wizard-nav-next',
      'button.btn-wizard-nav-submit',
      'button.formio-button-next',
      'button.formio-button-submit',
      'button.perinsubmitbtn'
    ].join(', ');

    const allButtons = Array.from(document.querySelectorAll<HTMLButtonElement>(selector));
    return Array.from(new Set(allButtons)).filter((button: HTMLButtonElement) => this.isWorkflowNavigationButton(button));
  }

  private isWorkflowNavigationButton(button: HTMLButtonElement): boolean {
    const className = String(button.className || '');
    if (this.wizardNavigationClassPattern.test(className)) {
      return true;
    }

    const label = String(
      button.textContent
      || button.innerText
      || button.getAttribute('aria-label')
      || button.getAttribute('title')
      || ''
    ).trim().toLowerCase();

    return label === 'next'
      || label === 'submit'
      || label === 'submit application'
      || label === 'save and next'
      || label === 'save & next'
      || className.includes('perinsubmitbtn');
  }

  shouldShowNewItemStar(row: SupportingDocumentRow): boolean {
    if (!this.coerceBooleanFlag(row?.is_new)) {
      return false;
    }
    const roleId = this.resolveCurrentUserRoleId();
    return roleId !== 405 && roleId !== '405';
  }

  isDocumentReadOnly(row: SupportingDocumentRow): boolean {
    const roleId = this.resolveCurrentUserRoleId();
    if (roleId !== 405 && roleId !== '405') {
      return false;
    }
    if((roleId === 405 || roleId === '405') && row.is_profile_document ){
      return true;
    }
    if((roleId === 405 || roleId === '405') && row.is_profile_document ){
      return true;
    }
    return this.coerceBooleanFlag(row?.is_read_only);
  }

  formatDocumentStatus(data: any): string {
    // Read applicationStatus defensively: it may be missing, an empty string,
    // a JSON-encoded string, or a plain string. JSON.parse('') / JSON.parse(plain)
    // throws and previously broke template bindings (status/category/date cells).
    let applicationStatus = '';
    try {
      const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('applicationStatus') : '';
      if (raw) {
        try {
          applicationStatus = JSON.parse(raw);
        } catch {
          applicationStatus = raw;
        }
      }
    } catch {
      applicationStatus = '';
    }
    const isApplicantDeficiencyStatus = ['Deficiency Pending + BG Pending', 'Deficiency Pending + BG Approved', 'Deficiency Pending + BG Denied', 'Deficiency Pending'].includes(applicationStatus);
    const isStafDeficiencyStatus = ['Intake Pending + BG Pending', 'Intake Pending + BG Approved', 'Intake Pending + BG Denied', 'Intake Pending', 'Backoffice Pending'].includes(applicationStatus);

    const roleId = this.resolveCurrentUserRoleId();
    // if (status == 'Deficient' || status == 'Verified') {
    //   if ((roleId === 405 || roleId === '405')&& isApplicantDeficiencyStatus) {
    //     return 'Pending Review';
    //   }
    // }
    // return data.status;
    let status;
    if ((roleId === 405 || roleId === '405')) {
      if (isStafDeficiencyStatus) {
        if (this.timelineIntakeStatusCount == 1) {
          status =  "Pending Review";
        }
        else if (this.timelineIntakeStatusCount > 1) {
          const isLatestGreater =this.latestIntakeOccuredDate && data.updated_date? new Date(this.latestIntakeOccuredDate).getTime() > new Date(data.updated_date).getTime():false;
          if (isLatestGreater) {
           status =  "Pending Review";
          } else {
           status =  data.status
          }
        }else {
        status = data.status
      }
      } else {
        status = data.status
      }

    } else if ((roleId != 405 && roleId != '405')) {
      if (isApplicantDeficiencyStatus) {
        status = data.status == "Pending Review" ? "Deficient" : data.status
      } else {
       status = data.status
      }
    }
    return status;
  }

  onRowPrepared(e: any): void {
    if (e.rowType !== 'data' || !e.data) {
      return;
    }
    const roleId = this.resolveCurrentUserRoleId();
    if (roleId === 405 || roleId === '405') {
      return;
    }
    const rowElement = e.rowElement as HTMLElement;
    if (this.coerceBooleanFlag(e.data.is_highlighted)) {
      rowElement.classList.add('highlighted-row');
      rowElement.addEventListener('mouseenter', () => {
        rowElement.style.backgroundColor = '#F4F8FF';
      });
      rowElement.addEventListener('mouseleave', () => {
        rowElement.style.backgroundColor = '#F4F8FF';
      });
    } else if (this.coerceBooleanFlag(e.data.is_highlighted_update)) {
      rowElement.classList.add('highlighted-row-update');
      rowElement.addEventListener('mouseenter', () => {
        rowElement.style.backgroundColor = '#FFFBEF';
      });
      rowElement.addEventListener('mouseleave', () => {
        rowElement.style.backgroundColor = '#FFFBEF';
      });
    }
  }

  private coerceBooleanFlag(value: any): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

}
